import {
  type BlockAnimation,
  type BlockCollection,
  type BlockOscillator,
  type BlockOscillatorAnimation,
  type BlockParametricAnimation,
  type BlockSequenceAnimation,
  type CollectionAccess,
  type BlockCollisionParams,
  type BlockNoiseParams,
  type CustomBlockDefinition,
  type LibraryBlockDefinition,
  type OscillatorAnchor,
  type OscillatorAxis,
  type OscillatorMode,
  type SequenceAnimationItem,
  type TexturePixels,
  type VoxelMaterial,
  ANIMATION_BLOCK_VIEW_ID,
  BLOCK_COLLISION_GROUPS,
  BLOCK_NOISE_BLEND_MODES,
  BLOCK_NOISE_TYPES,
  CORE_BLOCK_TYPES,
  CORE_COLLECTION,
  CORE_COLLECTION_ID,
  CRAFT_GRID_SIZE,
  DEFAULT_BLOCK_ANIMATION,
  DEFAULT_BLOCK_COLLISION,
  DEFAULT_BLOCK_NOISE,
  DEFAULT_OSCILLATOR,
  MAX_BLOCK_VIEW_SNAPSHOTS,
  MAX_OSCILLATORS,
  MAX_OSCILLATOR_AMPLITUDE,
  MAX_OSCILLATOR_PERIOD_TICKS,
  MAX_SEQUENCE_ITEMS,
  MAX_SEQUENCE_ITEM_TICKS,
  OSCILLATOR_ANCHORS,
  OSCILLATOR_AXES,
  OSCILLATOR_MODES,
  RANDOM_BLOCK_VIEW_ID,
  TEXTURE_GRID_SIZE,
  textureGridSizeFromPixels,
  customBlockType,
  isCoreCollectionId,
  libraryBlockType,
  type BlockViewSnapshotRef,
} from '@shared/protocol';
import { blockTypeById } from '../world/blocks';
import { createBlockIcon } from './BlockIcon';
import { BlockPreview3D } from './BlockPreview3D';
import {
  customBlockFileFromTextFile,
  downloadCustomBlockFile,
} from './customBlockFormat';
import { downloadTextureAtlas, texturePixelsFromImageFile } from './textureAtlas';

export interface InventoryHost {
  customBlocks: Map<number, CustomBlockDefinition>;
  libraryBlocks: Map<number, LibraryBlockDefinition>;
  collections: Map<number, BlockCollection>;
  types: number[];
  commitLoadout(types: number[], selectedSlot: number): void;
  selectedSlot: number;
  createCollection(
    name: string,
    description: string,
    access: CollectionAccess,
  ): Promise<BlockCollection>;
  updateCollection(
    id: number,
    name: string,
    description: string,
    access: CollectionAccess,
  ): Promise<BlockCollection>;
  deleteCollection(
    id: number,
    mode: 'keep-blocks' | 'delete-blocks',
  ): Promise<void>;
  createSolidBlock(
    name: string,
    color: string,
    opacity: number,
    collision?: BlockCollisionParams,
    glowIntensity?: number | null,
  ): Promise<LibraryBlockDefinition>;
  createTexturedBlock(
    name: string,
    color: string,
    pixels: TexturePixels,
    noise?: BlockNoiseParams | null,
    collision?: BlockCollisionParams,
  ): Promise<LibraryBlockDefinition>;
  createCustomBlock(
    name: string,
    gridSize: number,
    voxels: Array<[number, VoxelMaterial]>,
    noise?: BlockNoiseParams | null,
    collision?: BlockCollisionParams,
  ): Promise<CustomBlockDefinition>;
  updateSolidBlock(
    id: number,
    patch: {
      name: string;
      color?: string;
      opacity?: number;
      glowIntensity?: number | null;
      collectionId?: number | null;
      collision?: BlockCollisionParams;
      editViewId?: number;
    },
  ): Promise<LibraryBlockDefinition>;
  updateTexturedBlock(
    id: number,
    name: string,
    color: string,
    pixels: TexturePixels,
    collectionId?: number | null,
    noise?: BlockNoiseParams | null,
    collision?: BlockCollisionParams,
    editViewId?: number,
  ): Promise<LibraryBlockDefinition>;
  deleteLibraryBlock(id: number): Promise<void>;
  patchCustomBlock(
    id: number,
    patch: {
      name?: string;
      collectionId?: number | null;
      noise?: BlockNoiseParams | null;
      collision?: BlockCollisionParams;
      editViewId?: number;
    },
  ): Promise<CustomBlockDefinition>;
  updateCustomBlock(
    id: number,
    name: string,
    gridSize: number,
    voxels: Array<[number, VoxelMaterial]>,
    noise?: BlockNoiseParams | null,
    collision?: BlockCollisionParams,
    editViewId?: number,
  ): Promise<CustomBlockDefinition>;
  deleteCustomBlock(id: number): Promise<void>;
  createCustomBlockView(
    id: number,
    name?: string,
    sourceViewId?: number,
  ): Promise<CustomBlockDefinition>;
  activateCustomBlockView(id: number, viewId: number): Promise<CustomBlockDefinition>;
  renameCustomBlockView(
    id: number,
    viewId: number,
    name: string,
  ): Promise<CustomBlockDefinition>;
  deleteCustomBlockView(id: number, viewId: number): Promise<CustomBlockDefinition>;
  createLibraryBlockView(
    id: number,
    name?: string,
    sourceViewId?: number,
  ): Promise<LibraryBlockDefinition>;
  activateLibraryBlockView(id: number, viewId: number): Promise<LibraryBlockDefinition>;
  renameLibraryBlockView(
    id: number,
    viewId: number,
    name: string,
  ): Promise<LibraryBlockDefinition>;
  deleteLibraryBlockView(id: number, viewId: number): Promise<LibraryBlockDefinition>;
  updateCustomBlockAnimation(
    id: number,
    animation: BlockAnimation,
  ): Promise<CustomBlockDefinition>;
  updateLibraryBlockAnimation(
    id: number,
    animation: BlockAnimation,
  ): Promise<LibraryBlockDefinition>;
}

const NOISE_TYPE_LABELS: Record<(typeof BLOCK_NOISE_TYPES)[number], string> = {
  omni: 'Omni white noise',
  gradientBottomTop: 'Bottom → top gradient',
  gradientTopBottom: 'Top → bottom gradient',
  wavesSmall: 'Waves · small',
  wavesMedium: 'Waves · medium',
  wavesLarge: 'Waves · large',
};

const NOISE_BLEND_LABELS: Record<(typeof BLOCK_NOISE_BLEND_MODES)[number], string> = {
  normal: 'Normal',
  multiply: 'Multiply',
  screen: 'Screen',
  overlay: 'Overlay',
  softLight: 'Soft light',
  add: 'Add',
  subtract: 'Subtract',
  darken: 'Darken',
  lighten: 'Lighten',
  colorBurn: 'Color burn',
  colorDodge: 'Color dodge',
};

type PanelView =
  | { kind: 'tips' }
  | { kind: 'create-collection' }
  | { kind: 'core-collection' }
  | { kind: 'collection'; id: number }
  | { kind: 'create-solid' }
  | { kind: 'create-textured' }
  | { kind: 'create-custom' }
  | { kind: 'solid'; id: number }
  | { kind: 'textured'; id: number }
  | { kind: 'custom'; id: number }
  | { kind: 'builtin'; type: number };

interface InventoryCard {
  key: string;
  type: number;
  name: string;
  collectionId: number | null;
  selectable: PanelView;
}

type BlockTypeFilter = 'core' | 'solid' | 'textured' | 'custom';

const BLOCK_TYPE_FILTERS: ReadonlyArray<{ id: BlockTypeFilter; label: string }> = [
  { id: 'core', label: 'Core' },
  { id: 'solid', label: 'Solid color' },
  { id: 'textured', label: 'Textured' },
  { id: 'custom', label: 'Custom' },
];

const ALL_BLOCK_TYPE_FILTERS = new Set<BlockTypeFilter>(
  BLOCK_TYPE_FILTERS.map((entry) => entry.id),
);

const SWATCHES = [
  '#ffffff', '#e53935', '#fb8c00', '#fdd835', '#43a047',
  '#00acc1', '#1e88e5', '#8e24aa', '#6d4c41', '#212121',
];

const SEARCH_DEBOUNCE_MS = 1500;
const TIP_ROTATE_MS = 15000;

const INVENTORY_TIPS = [
  'Drag any block from the left list into your quick-access bar. Drag a quick-access block off the toolbar to remove it.',
  'Use + in Blocks to create solid, textured, or custom geometry blocks. Drafts are not stored until Save (textured may start white; custom needs uploaded geometry).',
  'Use Collections to group your blocks. Private and public are yours; external ones are shared read-only.',
  'Core holds shared basic blocks (including Craft). It cannot be edited.',
  'Paint textured cubes on a Craft block after creating them here, or fill all faces with a solid color in Inventory and layer noise on top.',
  'Deleting a block removes it from inventory and hotbar, but keeps placements in worlds.',
  'Textured blocks download/upload a Minecraft box-UV atlas (32×16 for 8×8 faces, 64×32 for 16×16). Uploading a 16×16 atlas switches that block to 16³ resolution; new blocks default to 8×8. Legacy 3×2 and single-face images still import.',
  'Custom geometry blocks download/upload .bfcb text (layers, colors, transparency). Schema and examples: related/formats/bfcb.md.',
] as const;

/** Split inventory window: left block grid, right entity editor/details. */
export class InventoryPanel {
  private collectionFilter: number | 'all' = 'all';
  private typeFilters = new Set<BlockTypeFilter>(ALL_BLOCK_TYPE_FILTERS);
  private searchQuery = '';
  private searchDraft = '';
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private tipTimer: ReturnType<typeof setInterval> | null = null;
  private tipIndex = 0;
  private preview3d: BlockPreview3D | null = null;
  /** Kept across remounts so snapshot switch / save keeps the orbit. */
  private previewOrbit: { yaw: number; pitch: number } | null = null;
  /**
   * View-tab snapshot being edited. Independent of Params `activeViewId`.
   * Kept across save remounts so appearance edits don't snap back.
   */
  private editingViewId: number | null = null;
  /** Detail tab kept across remounts (e.g. Params active-snapshot switch). */
  private detailTab: DetailTabId = 'view';
  private closeToolbarMenus: ((event: MouseEvent) => void) | null = null;
  private view: PanelView = { kind: 'tips' };

  constructor(
    private readonly root: HTMLElement,
    private readonly host: InventoryHost,
  ) {}

  render(): void {
    if (this.searchTimer !== null) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    this.clearTipRotation();
    this.disposePreview3D();
    if (this.closeToolbarMenus) {
      document.removeEventListener('click', this.closeToolbarMenus);
      this.closeToolbarMenus = null;
    }
    this.root.innerHTML = `
      <section class="inventory-window">
        <header class="inventory-titlebar">
          <strong>Inventory</strong>
          <em class="inventory-tip">Drag a block into your quick-access toolbar.</em>
          <button type="button" class="close-inventory" aria-label="Close inventory">×</button>
        </header>
        <div class="inventory-toolbar">
          <section class="toolbar-section collections-section" aria-label="Collections">
            <h3 class="toolbar-section-label">Collections</h3>
            <div class="toolbar-section-row">
              <label class="collection-filter-wrap">
                <span class="material-symbols-outlined collection-access-icon" aria-hidden="true">public</span>
                <select class="collection-filter" aria-label="Collection"></select>
              </label>
              <button type="button" class="toolbar-icon edit-collection" title="Select one of your collections to edit" aria-label="Edit collection" disabled><span class="material-symbols-outlined" aria-hidden="true">edit</span></button>
              <button type="button" class="toolbar-icon add-collection" title="Add collection" aria-label="Add collection"><span class="material-symbols-outlined" aria-hidden="true">add</span></button>
              <button type="button" class="toolbar-icon open-market" title="Market (coming soon)" aria-label="Open market" disabled><span class="material-symbols-outlined" aria-hidden="true">storefront</span></button>
            </div>
          </section>
          <section class="toolbar-section blocks-section" aria-label="Blocks">
            <h3 class="toolbar-section-label">Blocks</h3>
            <div class="toolbar-section-row">
              <label class="inventory-search">
                <span class="material-symbols-outlined search-icon" aria-hidden="true">search</span>
                <input class="search-input" type="search" placeholder="Search blocks" maxlength="64">
              </label>
              <div class="type-filter-menu">
                <button type="button" class="type-filter-button" aria-label="Filter block types" aria-haspopup="listbox" aria-expanded="false">
                  <span class="material-symbols-outlined type-filter-icon" aria-hidden="true">filter_list</span>
                  <span class="type-filter-label">All types</span>
                  <span class="material-symbols-outlined type-filter-caret" aria-hidden="true">expand_more</span>
                </button>
                <div class="type-filter-dropdown" hidden role="listbox" aria-multiselectable="true" aria-label="Block types">
                  ${BLOCK_TYPE_FILTERS.map((entry) => `
                    <label>
                      <input type="checkbox" value="${entry.id}">
                      <span>${entry.label}</span>
                    </label>`).join('')}
                </div>
              </div>
              <div class="add-block-menu">
                <button type="button" class="toolbar-icon add-block-button" title="Add block" aria-label="Add block"><span class="material-symbols-outlined" aria-hidden="true">add</span></button>
                <div class="add-block-dropdown" hidden>
                  <button type="button" data-kind="solid">Solid color block</button>
                  <button type="button" data-kind="textured">Textured block</button>
                  <button type="button" data-kind="custom">Custom geometry block</button>
                </div>
              </div>
            </div>
          </section>
        </div>
        <div class="inventory-split">
          <div class="inventory-body inventory-list-pane">
            <div class="inventory-grid"></div>
          </div>
          <aside class="inventory-detail-pane"></aside>
        </div>
      </section>`;

    this.bindChrome();
    this.fillCollectionFilter();
    this.fillGrid();
    this.renderDetail();
  }

  private bindChrome(): void {
    this.root.querySelector<HTMLButtonElement>('.close-inventory')!.onclick =
      (event) => {
        event.stopPropagation();
        this.root.dispatchEvent(new CustomEvent('inventory-close', { detail: { relock: true } }));
      };
    this.root.querySelector<HTMLButtonElement>('.add-collection')!.onclick =
      (event) => {
        event.stopPropagation();
        this.view = { kind: 'create-collection' };
        this.renderDetail();
      };
    this.root.querySelector<HTMLButtonElement>('.edit-collection')!.onclick =
      (event) => {
        event.stopPropagation();
        const button = event.currentTarget as HTMLButtonElement;
        if (button.disabled) return;
        if (this.collectionFilter === 'all' || isCoreCollectionId(this.collectionFilter)) {
          return;
        }
        const selected = this.selectedCollection();
        if (selected && isExternalCollection(selected)) return;
        this.view = { kind: 'collection', id: this.collectionFilter };
        this.renderDetail();
      };
    const filter = this.root.querySelector<HTMLSelectElement>('.collection-filter')!;
    filter.onchange = () => {
      const value = filter.value;
      if (value === 'all') {
        this.collectionFilter = 'all';
        this.view = { kind: 'tips' };
      } else if (Number(value) === CORE_COLLECTION_ID) {
        this.collectionFilter = CORE_COLLECTION_ID;
        this.view = { kind: 'core-collection' };
      } else {
        this.collectionFilter = Number(value);
        this.view = { kind: 'collection', id: this.collectionFilter };
      }
      this.syncCollectionControls();
      this.fillGrid();
      this.renderDetail();
    };
    const searchInput = this.root.querySelector<HTMLInputElement>('.search-input')!;
    searchInput.value = this.searchDraft || this.searchQuery;
    searchInput.oninput = () => {
      this.searchDraft = searchInput.value;
      if (this.searchTimer !== null) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.searchQuery = this.searchDraft.trim().toLowerCase();
        this.fillGrid();
      }, SEARCH_DEBOUNCE_MS);
    };
    const typeMenu = this.root.querySelector<HTMLElement>('.type-filter-menu')!;
    const typeButton = this.root.querySelector<HTMLButtonElement>('.type-filter-button')!;
    const typeDropdown = this.root.querySelector<HTMLElement>('.type-filter-dropdown')!;
    const addMenu = this.root.querySelector<HTMLElement>('.add-block-menu')!;
    const addButton = this.root.querySelector<HTMLButtonElement>('.add-block-button')!;
    const addDropdown = this.root.querySelector<HTMLElement>('.add-block-dropdown')!;
    typeButton.onclick = (event) => {
      event.stopPropagation();
      const open = typeDropdown.hidden;
      typeDropdown.hidden = !open;
      typeButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      addDropdown.hidden = true;
    };
    typeDropdown.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((input) => {
      const id = input.value as BlockTypeFilter;
      input.checked = this.typeFilters.has(id);
      input.onchange = (event) => {
        event.stopPropagation();
        if (input.checked) this.typeFilters.add(id);
        else this.typeFilters.delete(id);
        this.syncTypeFilterLabel();
        this.fillGrid();
      };
      input.onclick = (event) => event.stopPropagation();
    });
    typeDropdown.onclick = (event) => event.stopPropagation();
    this.syncTypeFilterLabel();
    addButton.onclick = (event) => {
      event.stopPropagation();
      if (addButton.disabled) return;
      addDropdown.hidden = !addDropdown.hidden;
      typeDropdown.hidden = true;
      typeButton.setAttribute('aria-expanded', 'false');
    };
    addDropdown.querySelectorAll<HTMLButtonElement>('button[data-kind]').forEach((button) => {
      button.onclick = (event) => {
        event.stopPropagation();
        addDropdown.hidden = true;
        const kind = button.dataset.kind;
        if (kind === 'textured' || kind === 'custom' || kind === 'solid') {
          this.startAddBlock(kind);
        }
      };
    });
    this.closeToolbarMenus = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!addMenu.contains(target)) addDropdown.hidden = true;
      if (!typeMenu.contains(target)) {
        typeDropdown.hidden = true;
        typeButton.setAttribute('aria-expanded', 'false');
      }
    };
    document.addEventListener('click', this.closeToolbarMenus);
  }

  private syncTypeFilterLabel(): void {
    const label = this.root.querySelector<HTMLElement>('.type-filter-label');
    const button = this.root.querySelector<HTMLButtonElement>('.type-filter-button');
    if (!label || !button) return;
    const selected = BLOCK_TYPE_FILTERS.filter((entry) => this.typeFilters.has(entry.id));
    let text: string;
    if (selected.length === 0) text = 'No types';
    else if (selected.length === BLOCK_TYPE_FILTERS.length) text = 'All types';
    else if (selected.length === 1) text = selected[0]!.label;
    else text = `${selected.length} types`;
    label.textContent = text;
    button.title = selected.length === 0
      ? 'No block types selected'
      : selected.map((entry) => entry.label).join(', ');
  }

  private cardMatchesTypeFilter(card: InventoryCard): boolean {
    const kind = card.selectable.kind;
    if (kind === 'builtin') return this.typeFilters.has('core');
    if (kind === 'solid') return this.typeFilters.has('solid');
    if (kind === 'textured') return this.typeFilters.has('textured');
    if (kind === 'custom') return this.typeFilters.has('custom');
    return false;
  }

  private fillCollectionFilter(): void {
    const filter = this.root.querySelector<HTMLSelectElement>('.collection-filter')!;
    filter.replaceChildren();
    const all = document.createElement('option');
    all.value = 'all';
    all.textContent = 'All';
    filter.appendChild(all);
    const core = document.createElement('option');
    core.value = String(CORE_COLLECTION_ID);
    core.textContent = CORE_COLLECTION.name;
    filter.appendChild(core);

    const mine = document.createElement('optgroup');
    mine.label = 'My collections';
    const external = document.createElement('optgroup');
    external.label = 'External';
    const collections = [...this.host.collections.values()]
      .filter((collection) => !isCoreCollectionId(collection.id))
      .sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
    for (const collection of collections) {
      const option = document.createElement('option');
      option.value = String(collection.id);
      option.textContent = `${accessLabel(collection.access)} · ${collection.name}`;
      option.dataset.access = collection.access;
      // External shared collections will land here once market/trade exists.
      if (isExternalCollection(collection)) {
        option.dataset.external = '1';
        option.textContent = `External · ${collection.name}`;
        external.appendChild(option);
      } else {
        mine.appendChild(option);
      }
    }
    if (mine.childElementCount > 0) filter.appendChild(mine);
    if (external.childElementCount > 0) filter.appendChild(external);

    filter.value = this.collectionFilter === 'all'
      ? 'all'
      : String(this.collectionFilter);
    if (filter.value !== (this.collectionFilter === 'all' ? 'all' : String(this.collectionFilter))) {
      this.collectionFilter = 'all';
      filter.value = 'all';
    }
    this.syncCollectionControls();
  }

  private syncCollectionControls(): void {
    const edit = this.root.querySelector<HTMLButtonElement>('.edit-collection')!;
    const addBlock = this.root.querySelector<HTMLButtonElement>('.add-block-button')!;
    const selected = this.selectedCollection();
    const external = selected !== null && isExternalCollection(selected);
    const canEdit =
      this.collectionFilter !== 'all'
      && !isCoreCollectionId(this.collectionFilter)
      && !external;
    edit.hidden = false;
    edit.disabled = !canEdit;
    edit.title = canEdit
      ? 'Edit collection'
      : external
        ? 'External collections are read-only'
        : 'Select one of your collections to edit';
    addBlock.disabled = external;
    addBlock.title = external
      ? 'External collections are read-only'
      : 'Add block';
    this.syncCollectionAccessIcon();
  }

  private selectedCollection(): BlockCollection | null {
    if (this.collectionFilter === 'all') return null;
    if (isCoreCollectionId(this.collectionFilter)) return CORE_COLLECTION;
    return this.host.collections.get(this.collectionFilter) ?? null;
  }

  private syncCollectionAccessIcon(): void {
    const icon = this.root.querySelector<HTMLElement>('.collection-access-icon');
    if (!icon) return;
    if (this.collectionFilter === 'all' || isCoreCollectionId(this.collectionFilter)) {
      icon.textContent = 'public';
      return;
    }
    const collection = this.host.collections.get(this.collectionFilter);
    if (!collection) {
      icon.textContent = 'public';
      return;
    }
    if (isExternalCollection(collection)) {
      icon.textContent = 'link';
      return;
    }
    icon.textContent = accessIcon(collection.access);
  }

  private fillGrid(): void {
    const grid = this.root.querySelector<HTMLElement>('.inventory-grid')!;
    grid.replaceChildren();
    const cards = this.visibleCards();
    for (const card of cards) {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'inventory-item';
      if (this.isSelectedCard(card)) item.classList.add('selected');
      item.draggable = true;
      item.title = `Select or drag ${card.name}`;
      item.append(createBlockIcon(
        card.type,
        this.host.customBlocks,
        this.host.libraryBlocks,
      ));
      const label = document.createElement('span');
      label.textContent = card.name;
      item.appendChild(label);
      item.ondragstart = (event) => {
        if (!event.dataTransfer) return;
        setDragPayload(event.dataTransfer, { source: 'inventory', type: card.type });
        const icon = item.querySelector<HTMLCanvasElement>('.block-icon');
        if (icon) setBlockDragImage(event, icon);
        item.classList.add('dragging');
      };
      item.ondragend = () => item.classList.remove('dragging');
      item.onclick = (event) => {
        event.stopPropagation();
        if (!this.isSelectedCard(card)) {
          this.editingViewId = null;
          this.detailTab = 'view';
        }
        this.view = card.selectable;
        this.fillGrid();
        this.renderDetail();
      };
      grid.appendChild(item);
    }
    const visibleSlots = Math.max(18, Math.ceil(cards.length / 3) * 3);
    for (let index = cards.length; index < visibleSlots; index++) {
      const empty = document.createElement('div');
      empty.className = 'inventory-item empty';
      empty.setAttribute('aria-hidden', 'true');
      grid.appendChild(empty);
    }
  }

  private visibleCards(): InventoryCard[] {
    const query = this.searchQuery;
    const filter = this.collectionFilter;
    const cards: InventoryCard[] = [];
    const coreSelected = filter !== 'all' && isCoreCollectionId(filter);
    if (filter === 'all' || coreSelected) {
      for (const type of CORE_BLOCK_TYPES) {
        cards.push({
          key: `builtin-${type}`,
          type,
          name: blockTypeById(type).name,
          collectionId: CORE_COLLECTION_ID,
          selectable: { kind: 'builtin', type },
        });
      }
    }
    if (coreSelected) {
      return cards
        .filter((card) => this.cardMatchesTypeFilter(card))
        .filter((card) => !query || card.name.toLowerCase().includes(query))
        .sort((a, b) => a.name.localeCompare(b.name) || a.type - b.type);
    }
    for (const block of this.host.libraryBlocks.values()) {
      if (filter !== 'all' && block.collectionId !== filter) continue;
      cards.push({
        key: `library-${block.id}`,
        type: libraryBlockType(block.id),
        name: block.name,
        collectionId: block.collectionId,
        selectable: block.kind === 'solid'
          ? { kind: 'solid', id: block.id }
          : { kind: 'textured', id: block.id },
      });
    }
    for (const block of this.host.customBlocks.values()) {
      if (filter !== 'all' && block.collectionId !== filter) continue;
      cards.push({
        key: `custom-${block.id}`,
        type: customBlockType(block.id),
        name: block.name,
        collectionId: block.collectionId,
        selectable: { kind: 'custom', id: block.id },
      });
    }
    return cards
      .filter((card) => this.cardMatchesTypeFilter(card))
      .filter((card) => !query || card.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name) || a.type - b.type);
  }

  private isSelectedCard(card: InventoryCard): boolean {
    const view = this.view;
    if (view.kind === 'solid' || view.kind === 'textured' || view.kind === 'custom') {
      return card.selectable.kind === view.kind &&
        'id' in card.selectable && card.selectable.id === view.id;
    }
    if (view.kind === 'builtin') {
      return card.selectable.kind === 'builtin' && card.selectable.type === view.type;
    }
    return false;
  }

  private renderDetail(): void {
    this.clearTipRotation();
    this.disposePreview3D();
    const pane = this.root.querySelector<HTMLElement>('.inventory-detail-pane')!;
    pane.replaceChildren();
    switch (this.view.kind) {
      case 'tips':
        pane.appendChild(this.tipsPanel());
        break;
      case 'create-collection':
        pane.appendChild(this.collectionForm(null));
        break;
      case 'core-collection':
        pane.appendChild(this.coreCollectionPanel());
        break;
      case 'collection':
        if (isCoreCollectionId(this.view.id)) {
          pane.appendChild(this.coreCollectionPanel());
          break;
        }
        pane.appendChild(this.collectionForm(this.host.collections.get(this.view.id) ?? null));
        break;
      case 'create-solid':
        pane.appendChild(this.solidForm(null));
        break;
      case 'create-textured':
        pane.appendChild(this.texturedForm(null));
        break;
      case 'create-custom':
        pane.appendChild(this.customForm(null));
        break;
      case 'solid':
        pane.appendChild(this.solidForm(
          this.host.libraryBlocks.get(this.view.id) as
            | Extract<LibraryBlockDefinition, { kind: 'solid' }>
            | undefined
            ?? null,
        ));
        break;
      case 'textured':
        pane.appendChild(this.texturedForm(this.host.libraryBlocks.get(this.view.id) ?? null));
        break;
      case 'custom':
        pane.appendChild(this.customForm(this.host.customBlocks.get(this.view.id) ?? null));
        break;
      case 'builtin':
        pane.appendChild(this.builtinForm(this.view.type));
        break;
    }
  }

  private startAddBlock(kind: 'solid' | 'textured' | 'custom'): void {
    this.editingViewId = null;
    this.detailTab = 'view';
    this.view = {
      kind: kind === 'solid'
        ? 'create-solid'
        : kind === 'textured'
          ? 'create-textured'
          : 'create-custom',
    };
    this.fillGrid();
    this.renderDetail();
  }

  private tipsPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'inventory-detail tips-detail';
    this.tipIndex = Math.floor(Math.random() * INVENTORY_TIPS.length);
    panel.innerHTML = `
      <h2>Inventory tips</h2>
      <article class="tip-card" aria-live="polite">
        <p class="tip-text"></p>
      </article>
      <div class="tip-nav">
        <button type="button" class="tip-arrow tip-prev" aria-label="Previous tip">
          <span class="material-symbols-outlined" aria-hidden="true">chevron_left</span>
        </button>
        <div class="tip-paginator" role="tablist" aria-label="Tip pages"></div>
        <button type="button" class="tip-arrow tip-next" aria-label="Next tip">
          <span class="material-symbols-outlined" aria-hidden="true">chevron_right</span>
        </button>
      </div>`;
    const paginator = panel.querySelector<HTMLElement>('.tip-paginator')!;
    for (let i = 0; i < INVENTORY_TIPS.length; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'tip-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Tip ${i + 1}`);
      dot.onclick = () => this.showTip(panel, i, true);
      paginator.appendChild(dot);
    }
    panel.querySelector<HTMLButtonElement>('.tip-prev')!.onclick =
      () => this.showTip(panel, this.tipIndex - 1, true);
    panel.querySelector<HTMLButtonElement>('.tip-next')!.onclick =
      () => this.showTip(panel, this.tipIndex + 1, true);
    this.showTip(panel, this.tipIndex, false);
    this.tipTimer = setInterval(() => {
      this.showTip(panel, this.tipIndex + 1, false);
    }, TIP_ROTATE_MS);
    return panel;
  }

  private showTip(panel: HTMLElement, index: number, resetTimer: boolean): void {
    const count = INVENTORY_TIPS.length;
    this.tipIndex = ((index % count) + count) % count;
    const text = panel.querySelector<HTMLElement>('.tip-text');
    if (text) text.textContent = INVENTORY_TIPS[this.tipIndex]!;
    panel.querySelectorAll<HTMLButtonElement>('.tip-dot').forEach((dot, i) => {
      const active = i === this.tipIndex;
      dot.classList.toggle('active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (resetTimer && this.tipTimer !== null) {
      clearInterval(this.tipTimer);
      this.tipTimer = setInterval(() => {
        this.showTip(panel, this.tipIndex + 1, false);
      }, TIP_ROTATE_MS);
    }
  }

  private clearTipRotation(): void {
    if (this.tipTimer !== null) {
      clearInterval(this.tipTimer);
      this.tipTimer = null;
    }
  }

  private disposePreview3D(): void {
    if (this.preview3d) {
      this.previewOrbit = this.preview3d.getOrbit();
      this.preview3d.dispose();
    }
    this.preview3d = null;
  }

  private mountPreview3D(previewHost: HTMLElement): BlockPreview3D {
    this.disposePreview3D();
    this.preview3d = new BlockPreview3D(previewHost);
    if (this.previewOrbit) {
      this.preview3d.setOrbit(this.previewOrbit.yaw, this.previewOrbit.pitch);
    }
    return this.preview3d;
  }

  private coreCollectionPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'inventory-detail';
    panel.innerHTML = `
      <h2>${escapeHtml(CORE_COLLECTION.name)}</h2>
      <p>${escapeHtml(CORE_COLLECTION.description)}</p>
      <p>Access: Public (system)</p>
      <ul class="inventory-tips">
        ${CORE_BLOCK_TYPES.map((type) =>
          `<li>${escapeHtml(blockTypeById(type).name)}</li>`).join('')}
      </ul>
      <p>You can use these blocks, but you cannot rename, delete, or move them.</p>`;
    return panel;
  }

  private collectionForm(collection: BlockCollection | null): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'inventory-detail';
    if (collection && isCoreCollectionId(collection.id)) {
      return this.coreCollectionPanel();
    }
    const creating = collection === null;
    panel.innerHTML = `
      <h2>${creating ? 'Add collection' : 'Edit collection'}</h2>
      <label>Name<input class="field-name" maxlength="64"></label>
      <label>Description<textarea class="field-description" maxlength="512" rows="4"></textarea></label>
      <label>Access
        <select class="field-access">
          <option value="private">Private</option>
          <option value="friends">For friends</option>
          <option value="public">Public</option>
        </select>
      </label>
      <p class="detail-error" role="alert"></p>
      <div class="detail-actions">
        <button type="button" class="detail-secondary cancel-detail">Cancel</button>
        <button type="button" class="detail-primary save-detail">Save</button>
      </div>
      ${creating ? '' : `
        <div class="detail-danger-zone">
          <h3>Delete collection</h3>
          <button type="button" class="detail-secondary delete-keep">Delete and keep blocks</button>
          <button type="button" class="detail-danger delete-blocks">Delete with all blocks</button>
        </div>`}`;
    const name = panel.querySelector<HTMLInputElement>('.field-name')!;
    const description = panel.querySelector<HTMLTextAreaElement>('.field-description')!;
    const access = panel.querySelector<HTMLSelectElement>('.field-access')!;
    const error = panel.querySelector<HTMLElement>('.detail-error')!;
    name.value = collection?.name ?? '';
    description.value = collection?.description ?? '';
    access.value = collection?.access ?? 'private';
    panel.querySelector<HTMLButtonElement>('.cancel-detail')!.onclick = () => {
      this.view = { kind: 'tips' };
      this.renderDetail();
    };
    panel.querySelector<HTMLButtonElement>('.save-detail')!.onclick = async () => {
      const nextName = name.value.trim();
      if (!nextName) {
        error.textContent = 'Enter a collection name.';
        return;
      }
      try {
        if (creating) {
          const created = await this.host.createCollection(
            nextName,
            description.value.trim(),
            access.value as CollectionAccess,
          );
          this.collectionFilter = created.id;
          this.view = { kind: 'collection', id: created.id };
        } else {
          await this.host.updateCollection(
            collection.id,
            nextName,
            description.value.trim(),
            access.value as CollectionAccess,
          );
          this.view = { kind: 'collection', id: collection.id };
        }
        this.fillCollectionFilter();
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not save collection.';
      }
    };
    panel.querySelector<HTMLButtonElement>('.delete-keep')?.addEventListener('click', () => {
      void this.removeCollection(collection!, 'keep-blocks');
    });
    panel.querySelector<HTMLButtonElement>('.delete-blocks')?.addEventListener('click', () => {
      void this.removeCollection(collection!, 'delete-blocks');
    });
    name.focus();
    return panel;
  }

  private async removeCollection(
    collection: BlockCollection,
    mode: 'keep-blocks' | 'delete-blocks',
  ): Promise<void> {
    const message = mode === 'delete-blocks'
      ? `Delete “${collection.name}” and archive all of its blocks from inventory? World placements stay.`
      : `Delete “${collection.name}” and keep its blocks uncategorized?`;
    if (!window.confirm(message)) return;
    try {
      await this.host.deleteCollection(collection.id, mode);
      this.collectionFilter = 'all';
      this.view = { kind: 'tips' };
      this.fillCollectionFilter();
      this.fillGrid();
      this.renderDetail();
    } catch (reason) {
      window.alert(reason instanceof Error ? reason.message : 'Could not delete collection.');
    }
  }

  private solidForm(
    block: Extract<LibraryBlockDefinition, { kind: 'solid' }> | null,
  ): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'inventory-detail has-detail-tabs';
    const creating = block === null;
    const initialCollision = block?.collision ?? DEFAULT_BLOCK_COLLISION;
    panel.innerHTML = `
      ${detailTabsHtml(!creating, this.detailTab)}
      <div class="detail-preview-pin" data-view-preview>
        <div class="detail-preview color-block-preview"></div>
      </div>
      <div class="detail-body">
        <div class="detail-tab-panel" data-panel="view">
          ${creating ? '' : viewSnapshotsManageHtml(
            block.views,
            this.resolveEditingViewId(block.views),
          )}
          <details class="detail-section" open>
            <summary>Common</summary>
            <div class="detail-section-body">
              <label>Name<input class="field-name" maxlength="64"></label>
              ${creating ? '' : `
                <label>Collection${this.collectionSelectHtml(block.collectionId)}</label>
              `}
            </div>
          </details>
          <details class="detail-section" open>
            <summary>Color</summary>
            <div class="detail-section-body">
              <label>Color
                <div class="color-input-row">
                  <input type="color" class="color-wheel" value="#ffffff">
                  <input class="color-hex" value="#ffffff" maxlength="7" spellcheck="false">
                </div>
              </label>
              <label>Transparency <output class="transparency-value">0%</output>
                <input type="range" class="color-transparency" min="0" max="95" value="0">
              </label>
              <label>Glow intensity <output class="glow-value">Off</output>
                <input type="range" class="field-glow-intensity" min="0" max="100" value="0">
              </label>
              <p class="detail-hint">Above 0, this solid emits an unshadowed point light (capped / distance-limited for performance). Glow paints into custom-geometry voxels, not textured faces.</p>
              <div class="color-swatches" aria-label="Color swatches"></div>
            </div>
          </details>
        </div>
        <div class="detail-tab-panel" data-panel="params" hidden>
          ${creating ? '' : activeSnapshotHtml(block.views, block.activeViewId)}
          ${collisionSectionHtml(initialCollision, false)}
        </div>
        ${creating ? '' : animateTabPanelHtml(block!.views, block!.animation, {})}
      </div>
      <div class="detail-footer">
        <p class="detail-error" role="alert"></p>
        <div class="detail-actions">
          <button type="button" class="detail-secondary cancel-detail">Cancel</button>
          <button type="button" class="detail-primary save-detail">Save</button>
          ${creating ? '' : `
            <button type="button" class="detail-danger delete-block">Delete block</button>`}
        </div>
      </div>`;
    wireDetailTabs(panel, this.detailTab, (tab) => {
      this.detailTab = tab;
    });
    const name = panel.querySelector<HTMLInputElement>('.field-name')!;
    const preview = panel.querySelector<HTMLElement>('.detail-preview')!;
    const preview3d = this.mountPreview3D(preview);
    const error = panel.querySelector<HTMLElement>('.detail-error')!;
    const readAnimation = !creating && block
      ? wireAnimationEditor(panel, block.views, block.animation ?? DEFAULT_BLOCK_ANIMATION, {})
      : null;
    const wheel = panel.querySelector<HTMLInputElement>('.color-wheel')!;
    const hex = panel.querySelector<HTMLInputElement>('.color-hex')!;
    const transparency = panel.querySelector<HTMLInputElement>('.color-transparency')!;
    const transparencyValue = panel.querySelector<HTMLOutputElement>('.transparency-value')!;
    const glowInput = panel.querySelector<HTMLInputElement>('.field-glow-intensity')!;
    const glowValue = panel.querySelector<HTMLOutputElement>('.glow-value')!;
    name.value = block?.name ?? `Color block #${this.host.libraryBlocks.size + 1}`;
    const readGlow = (): number | null => {
      const percent = Number(glowInput.value);
      if (!Number.isFinite(percent) || percent <= 0) return null;
      return Math.min(1, percent / 100);
    };
    const applyGlowLook = (glowIntensity: number | null) => {
      const percent = glowIntensity == null || glowIntensity <= 0
        ? 0
        : Math.round(glowIntensity * 100);
      glowInput.value = String(percent);
      glowValue.value = percent <= 0 ? 'Off' : `${percent}%`;
    };
    const applySolidLook = (
      color: string,
      opacity: number,
      glowIntensity: number | null = null,
    ) => {
      wheel.value = color;
      hex.value = color;
      const percent = Math.round((1 - opacity) * 100);
      transparency.value = String(percent);
      transparencyValue.value = `${percent}%`;
      applyGlowLook(glowIntensity);
    };
    const loadManageSnapshot = (viewId: number) => {
      const snap = block?.snapshots?.find((item) => item.id === viewId);
      if (snap?.kind === 'solid') {
        applySolidLook(snap.color, snap.opacity, snap.glowIntensity ?? null);
        return;
      }
      if (block) applySolidLook(block.color, block.opacity, block.glowIntensity);
    };
    if (!creating && block) {
      const manageId = this.resolveEditingViewId(block.views);
      this.editingViewId = manageId || null;
      loadManageSnapshot(manageId);
    } else {
      applySolidLook('#ffffff', 1, null);
    }
    const redraw = () => {
      const color = normalizeHex(hex.value) ?? '#ffffff';
      const opacity = 1 - Number(transparency.value) / 100;
      preview3d.setSubject({ kind: 'solid', color, opacity, glowIntensity: readGlow() });
    };
    const readEditViewId = (): number | undefined => {
      const manage = panel.querySelector<HTMLSelectElement>('.field-manage-snapshot');
      const viewId = Number(manage?.value);
      if (Number.isInteger(viewId) && viewId > 0) {
        this.editingViewId = viewId;
        return viewId;
      }
      return this.editingViewId ?? undefined;
    };
    const setColor = (value: string) => {
      const normalized = normalizeHex(value);
      if (!normalized) return;
      wheel.value = normalized;
      hex.value = normalized;
      redraw();
    };
    wheel.oninput = () => setColor(wheel.value);
    hex.oninput = () => {
      if (normalizeHex(hex.value)) setColor(hex.value);
    };
    transparency.oninput = () => {
      transparencyValue.value = `${transparency.value}%`;
      redraw();
    };
    glowInput.oninput = () => {
      applyGlowLook(readGlow());
      redraw();
    };
    const swatchRoot = panel.querySelector<HTMLElement>('.color-swatches')!;
    for (const color of SWATCHES) {
      const button = document.createElement('button');
      button.type = 'button';
      button.style.background = color;
      button.title = color;
      button.onclick = () => setColor(color);
      swatchRoot.appendChild(button);
    }
    name.oninput = () => redraw();
    panel.querySelector<HTMLButtonElement>('.cancel-detail')!.onclick = () => {
      this.view = { kind: 'tips' };
      this.renderDetail();
      this.fillGrid();
    };
    panel.querySelector<HTMLButtonElement>('.save-detail')!.onclick = async () => {
      const blockName = name.value.trim();
      if (!blockName) {
        error.textContent = 'Enter a block name.';
        return;
      }
      const color = normalizeHex(hex.value);
      if (!color) {
        error.textContent = 'Enter a valid six-digit hex color.';
        return;
      }
      const opacity = 1 - Number(transparency.value) / 100;
      const glowIntensity = readGlow();
      const collision = readCollision(panel, false);
      try {
        if (creating) {
          const created = await this.host.createSolidBlock(
            blockName, color, opacity, collision, glowIntensity,
          );
          this.view = { kind: 'solid', id: created.id };
        } else {
          const collectionSelect = panel.querySelector<HTMLSelectElement>('.field-collection');
          await this.host.updateSolidBlock(block.id, {
            name: blockName,
            color,
            opacity,
            glowIntensity,
            collectionId: readCollectionSelect(collectionSelect),
            collision,
            editViewId: readEditViewId(),
          });
          if (readAnimation) {
            await this.host.updateLibraryBlockAnimation(block.id, readAnimation());
          }
          this.view = { kind: 'solid', id: block.id };
        }
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not save block.';
      }
    };
    panel.querySelector<HTMLButtonElement>('.delete-block')?.addEventListener('click', () => {
      if (block) void this.removeLibrary(block.id, block.name);
    });
    if (!creating && block) {
      this.wireLibraryViewSnapshots(panel, block, error, async () => {
        // Persist current form into the View-tab snapshot before branching.
        const blockName = name.value.trim();
        const color = normalizeHex(hex.value);
        if (!blockName || !color) {
          throw new Error('Save a valid name and color before creating a snapshot.');
        }
        await this.host.updateSolidBlock(block.id, {
          name: blockName,
          color,
          opacity: 1 - Number(transparency.value) / 100,
          glowIntensity: readGlow(),
          collectionId: readCollectionSelect(
            panel.querySelector<HTMLSelectElement>('.field-collection'),
          ),
          collision: readCollision(panel, false),
          editViewId: readEditViewId(),
        });
      }, (viewId) => {
        loadManageSnapshot(viewId);
        redraw();
      });
    }
    redraw();
    name.focus();
    return panel;
  }

  private texturedForm(block: LibraryBlockDefinition | null): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'inventory-detail has-detail-tabs';
    const creating = block === null;
    if (!creating && block.kind !== 'textured') {
      panel.className = 'inventory-detail';
      panel.innerHTML = '<p class="detail-error">Textured block not found.</p>';
      return panel;
    }
    const textured = creating ? null : block;
    const initialNoise = (textured?.kind === 'textured' ? textured.noise : null)
      ?? DEFAULT_BLOCK_NOISE;
    // Prefer stored seed; new drafts default to 0; legacy falls back to block id.
    const initialSeed = clampNoiseSeed(
      initialNoise.seed ?? (creating ? 0 : (textured?.id ?? 0)),
    );
    const initialCollision = (textured?.kind === 'textured' ? textured.collision : null)
      ?? DEFAULT_BLOCK_COLLISION;
    panel.innerHTML = `
      ${detailTabsHtml(!creating, this.detailTab)}
      <div class="detail-preview-pin" data-view-preview>
        <div class="detail-preview color-block-preview"></div>
      </div>
      <div class="detail-body">
        <div class="detail-tab-panel" data-panel="view">
          ${creating || !textured ? '' : viewSnapshotsManageHtml(
            textured.views,
            this.resolveEditingViewId(textured.views),
          )}
          <details class="detail-section" open>
            <summary>Common</summary>
            <div class="detail-section-body">
              <label>Name<input class="field-name" maxlength="64"></label>
              ${creating ? '' : `
                <label>Collection${this.collectionSelectHtml(textured!.collectionId)}</label>
              `}
              <p class="detail-hint">${creating
                ? 'Pick a color (or upload a texture), tune noise, then Save. You can also paint on a Craft block after saving.'
                : 'Paint this texture on a Craft block: select it, then RMB an empty craft workspace.'}</p>
            </div>
          </details>
          <details class="detail-section" open>
            <summary>Base color</summary>
            <div class="detail-section-body">
              <label>Color
                <div class="color-input-row">
                  <input type="color" class="color-wheel" value="#ffffff">
                  <input class="color-hex" value="#ffffff" maxlength="7" spellcheck="false">
                </div>
              </label>
              <label>Transparency <output class="transparency-value">0%</output>
                <input type="range" class="color-transparency" min="0" max="95" value="0">
              </label>
              <div class="color-swatches" aria-label="Color swatches"></div>
            </div>
          </details>
          <details class="detail-section" open>
            <summary>Noise</summary>
            <div class="detail-section-body">
              <label>Type
                <select class="field-noise-type">
                  ${BLOCK_NOISE_TYPES.map((type) =>
                    `<option value="${type}">${NOISE_TYPE_LABELS[type]}</option>`).join('')}
                </select>
              </label>
              <label>Blending mode
                <select class="field-noise-blend">
                  ${BLOCK_NOISE_BLEND_MODES.map((mode) =>
                    `<option value="${mode}">${NOISE_BLEND_LABELS[mode]}</option>`).join('')}
                </select>
              </label>
              <label>Intensity
                <output class="noise-intensity-value">0%</output>
                <input class="field-noise-intensity" type="range" min="0" max="100" step="1" value="0">
              </label>
              ${noiseSeedHtml(initialSeed)}
              <p class="detail-hint">Noise is visual only — original texture pixels stay stored. Set intensity to 0 to remove it.</p>
            </div>
          </details>
          <details class="detail-section">
            <summary>Texture file</summary>
            <div class="detail-section-body">
              <p class="detail-hint">Download or upload a Minecraft box-UV atlas. Uploading replaces the current face pixels.</p>
              <div class="detail-actions">
                <button type="button" class="detail-secondary download-texture">Download texture</button>
                <button type="button" class="detail-secondary upload-texture">Upload texture</button>
                <input type="file" class="texture-file" accept="image/png,image/jpeg,image/webp" hidden>
              </div>
            </div>
          </details>
        </div>
        <div class="detail-tab-panel" data-panel="params" hidden>
          ${creating || !textured ? '' : activeSnapshotHtml(textured.views, textured.activeViewId)}
          ${collisionSectionHtml(initialCollision, false)}
        </div>
        ${creating || !textured ? '' : animateTabPanelHtml(textured.views, textured.animation, { allowParametric: true })}
      </div>
      <div class="detail-footer">
        <p class="detail-error" role="alert"></p>
        <div class="detail-actions">
          <button type="button" class="detail-secondary cancel-detail">Cancel</button>
          <button type="button" class="detail-primary save-detail">Save</button>
          ${creating ? '' : `
            <button type="button" class="detail-danger delete-block">Delete block</button>`}
        </div>
      </div>`;
    wireDetailTabs(panel, this.detailTab, (tab) => {
      this.detailTab = tab;
    });
    const name = panel.querySelector<HTMLInputElement>('.field-name')!;
    const preview = panel.querySelector<HTMLElement>('.detail-preview')!;
    const preview3d = this.mountPreview3D(preview);
    const error = panel.querySelector<HTMLElement>('.detail-error')!;
    const readAnimation = !creating && textured
      ? wireAnimationEditor(
        panel,
        textured.views,
        textured.animation ?? DEFAULT_BLOCK_ANIMATION,
        { allowParametric: true },
      )
      : null;
    const fileInput = panel.querySelector<HTMLInputElement>('.texture-file')!;
    const downloadButton = panel.querySelector<HTMLButtonElement>('.download-texture')!;
    const wheel = panel.querySelector<HTMLInputElement>('.color-wheel')!;
    const hex = panel.querySelector<HTMLInputElement>('.color-hex')!;
    const transparency = panel.querySelector<HTMLInputElement>('.color-transparency')!;
    const transparencyValue = panel.querySelector<HTMLOutputElement>('.transparency-value')!;
    const noiseType = panel.querySelector<HTMLSelectElement>('.field-noise-type')!;
    const noiseBlend = panel.querySelector<HTMLSelectElement>('.field-noise-blend')!;
    const noiseIntensity = panel.querySelector<HTMLInputElement>('.field-noise-intensity')!;
    const noiseIntensityOut = panel.querySelector<HTMLOutputElement>('.noise-intensity-value')!;
    name.value = textured?.name ?? `Textured block #${this.host.libraryBlocks.size + 1}`;
    noiseType.value = initialNoise.type;
    noiseBlend.value = initialNoise.blend;
    noiseIntensity.value = String(initialNoise.intensity);
    noiseIntensityOut.value = `${initialNoise.intensity}%`;
    let pixels: TexturePixels = textured
      ? textured.pixels.map((face) => [...face])
      : solidFillTexture('#ffffffff');
    const noiseSeed = panel.querySelector<HTMLInputElement>('.field-noise-seed')!;
    const noiseSeedOut = panel.querySelector<HTMLOutputElement>('.noise-seed-value')!;
    const applyNoiseFields = (noise: BlockNoiseParams) => {
      noiseType.value = noise.type;
      noiseBlend.value = noise.blend;
      noiseIntensity.value = String(noise.intensity);
      noiseIntensityOut.value = `${noise.intensity}%`;
      const seed = clampNoiseSeed(noise.seed ?? (textured?.id ?? 0));
      noiseSeed.value = String(seed);
      noiseSeedOut.value = String(seed);
    };
    const applyTexturedLook = (
      color: string,
      opacity: number,
      nextPixels: TexturePixels,
      noise: BlockNoiseParams | null,
    ) => {
      wheel.value = color;
      hex.value = color;
      const percent = Math.round((1 - opacity) * 100);
      transparency.value = String(percent);
      transparencyValue.value = `${percent}%`;
      pixels = nextPixels.map((face) => [...face]);
      applyNoiseFields(noise ?? DEFAULT_BLOCK_NOISE);
    };
    const loadManageSnapshot = (viewId: number) => {
      const snap = textured?.snapshots?.find((item) => item.id === viewId);
      if (snap?.kind === 'textured') {
        applyTexturedLook(snap.color, snap.opacity, snap.pixels, snap.noise);
        return;
      }
      if (textured?.kind === 'textured') {
        applyTexturedLook(
          textured.color,
          textured.opacity,
          textured.pixels,
          textured.noise,
        );
      }
    };
    if (!creating && textured) {
      const manageId = this.resolveEditingViewId(textured.views);
      this.editingViewId = manageId || null;
      loadManageSnapshot(manageId);
    } else {
      applyTexturedLook(
        textured?.color ?? '#ffffff',
        textured?.opacity ?? 1,
        pixels,
        initialNoise,
      );
    }
    const readNoise = (): BlockNoiseParams => ({
      type: noiseType.value as BlockNoiseParams['type'],
      blend: noiseBlend.value as BlockNoiseParams['blend'],
      intensity: Math.max(0, Math.min(100, Number(noiseIntensity.value) || 0)),
      seed: clampNoiseSeed(Number(noiseSeed.value) || 0),
    });
    const readFillColor = (): string => {
      const color = normalizeHex(hex.value) ?? '#ffffff';
      const opacity = 1 - Number(transparency.value) / 100;
      return `${color}${alphaHex(opacity)}`;
    };
    const readEditViewId = (): number | undefined => {
      const manage = panel.querySelector<HTMLSelectElement>('.field-manage-snapshot');
      const viewId = Number(manage?.value);
      if (Number.isInteger(viewId) && viewId > 0) {
        this.editingViewId = viewId;
        return viewId;
      }
      return this.editingViewId ?? undefined;
    };
    const redraw = () => {
      const gridSize = textureGridSizeFromPixels(pixels)
        ?? textured?.gridSize
        ?? TEXTURE_GRID_SIZE;
      preview3d.setSubject({
        kind: 'textured',
        pixels,
        gridSize,
        noise: readNoise(),
        id: textured?.id ?? 0,
      });
    };
    const fillAllFaces = () => {
      const gridSize = textureGridSizeFromPixels(pixels)
        ?? textured?.gridSize
        ?? TEXTURE_GRID_SIZE;
      pixels = solidFillTexture(readFillColor(), gridSize);
      error.textContent = '';
      redraw();
    };
    const setColor = (value: string) => {
      const normalized = normalizeHex(value);
      if (!normalized) return;
      wheel.value = normalized;
      hex.value = normalized;
      fillAllFaces();
    };
    wheel.oninput = () => setColor(wheel.value);
    hex.oninput = () => {
      if (normalizeHex(hex.value)) setColor(hex.value);
    };
    transparency.oninput = () => {
      transparencyValue.value = `${transparency.value}%`;
      fillAllFaces();
    };
    const swatchRoot = panel.querySelector<HTMLElement>('.color-swatches')!;
    for (const color of SWATCHES) {
      const button = document.createElement('button');
      button.type = 'button';
      button.style.background = color;
      button.title = color;
      button.onclick = () => setColor(color);
      swatchRoot.appendChild(button);
    }
    const onNoiseChange = () => {
      noiseIntensityOut.value = `${noiseIntensity.value}%`;
      noiseSeedOut.value = String(noiseSeed.value);
      redraw();
    };
    noiseType.onchange = onNoiseChange;
    noiseBlend.onchange = onNoiseChange;
    noiseIntensity.oninput = onNoiseChange;
    noiseSeed.oninput = onNoiseChange;
    redraw();
    panel.querySelector<HTMLButtonElement>('.cancel-detail')!.onclick = () => {
      this.view = { kind: 'tips' };
      this.fillGrid();
      this.renderDetail();
    };
    panel.querySelector<HTMLButtonElement>('.save-detail')!.onclick = async () => {
      const blockName = name.value.trim();
      if (!blockName) {
        error.textContent = 'Enter a block name.';
        return;
      }
      if (!normalizeHex(hex.value)) {
        error.textContent = 'Enter a valid six-digit hex color.';
        return;
      }
      const noise = readNoise();
      const color = readFillColor();
      try {
        if (creating) {
          const created = await this.host.createTexturedBlock(
            blockName,
            color,
            pixels,
            noise,
            readCollision(panel, false),
          );
          this.view = { kind: 'textured', id: created.id };
        } else {
          const collectionSelect = panel.querySelector<HTMLSelectElement>('.field-collection');
          const updated = await this.host.updateTexturedBlock(
            textured!.id,
            blockName,
            color,
            pixels,
            readCollectionSelect(collectionSelect),
            noise,
            readCollision(panel, false),
            readEditViewId(),
          );
          if (readAnimation) {
            await this.host.updateLibraryBlockAnimation(textured!.id, readAnimation());
          }
          this.view = { kind: 'textured', id: updated.id };
        }
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not save block.';
      }
    };
    downloadButton.onclick = () => {
      downloadTextureAtlas(name.value || textured?.name || 'texture', pixels);
    };
    panel.querySelector<HTMLButtonElement>('.upload-texture')!.onclick = () => fileInput.click();
    fileInput.onchange = async () => {
      const file = fileInput.files?.[0];
      fileInput.value = '';
      if (!file) return;
      try {
        pixels = await texturePixelsFromImageFile(file);
        const gridSize = textureGridSizeFromPixels(pixels) ?? TEXTURE_GRID_SIZE;
        redraw();
        error.textContent =
          `Texture loaded at ${gridSize}×${gridSize} — click Save to apply.`;
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not read image.';
      }
    };
    panel.querySelector<HTMLButtonElement>('.delete-block')?.addEventListener('click', () => {
      if (textured) void this.removeLibrary(textured.id, textured.name);
    });
    if (!creating && textured && textured.kind === 'textured') {
      this.wireLibraryViewSnapshots(panel, textured, error, async () => {
        const blockName = name.value.trim();
        if (!blockName || !normalizeHex(hex.value)) {
          throw new Error('Save a valid name and color before creating a snapshot.');
        }
        await this.host.updateTexturedBlock(
          textured.id,
          blockName,
          readFillColor(),
          pixels,
          readCollectionSelect(panel.querySelector<HTMLSelectElement>('.field-collection')),
          readNoise(),
          readCollision(panel, false),
          readEditViewId(),
        );
      }, (viewId) => {
        loadManageSnapshot(viewId);
        redraw();
      });
    }
    name.focus();
    return panel;
  }

  private customForm(block: CustomBlockDefinition | null): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'inventory-detail has-detail-tabs';
    const creating = block === null;
    const initialNoise = block?.noise ?? DEFAULT_BLOCK_NOISE;
    // Prefer stored seed; new drafts default to 0; legacy falls back to block id.
    const initialSeed = clampNoiseSeed(
      initialNoise.seed ?? (creating ? 0 : (block?.id ?? 0)),
    );
    const initialCollision = block?.collision ?? DEFAULT_BLOCK_COLLISION;
    panel.innerHTML = `
      ${detailTabsHtml(!creating, this.detailTab)}
      <div class="detail-preview-pin" data-view-preview>
        <div class="detail-preview color-block-preview"></div>
      </div>
      <div class="detail-body">
        <div class="detail-tab-panel" data-panel="view">
          ${creating || !block ? '' : viewSnapshotsManageHtml(
            block.views,
            this.resolveEditingViewId(block.views),
          )}
          <details class="detail-section" open>
            <summary>Common</summary>
            <div class="detail-section-body">
              <label>Name<input class="field-name" maxlength="64"></label>
              ${creating ? '' : `
                <label>Collection${this.collectionSelectHtml(block.collectionId)}</label>
              `}
              <p class="detail-hint">${creating
                ? 'Upload a .bfcb file, then Save. The block appears in inventory only after saving.'
                : 'Download/upload .bfcb text (layers + colors/transparency). Schema: related/formats/bfcb.md'}</p>
            </div>
          </details>
          <details class="detail-section" open>
            <summary>Noise</summary>
            <div class="detail-section-body">
              <label>Type
                <select class="field-noise-type">
                  ${BLOCK_NOISE_TYPES.map((type) =>
                    `<option value="${type}">${NOISE_TYPE_LABELS[type]}</option>`).join('')}
                </select>
              </label>
              <label>Blending mode
                <select class="field-noise-blend">
                  ${BLOCK_NOISE_BLEND_MODES.map((mode) =>
                    `<option value="${mode}">${NOISE_BLEND_LABELS[mode]}</option>`).join('')}
                </select>
              </label>
              <label>Intensity
                <output class="noise-intensity-value">0%</output>
                <input class="field-noise-intensity" type="range" min="0" max="100" step="1" value="0">
              </label>
              ${noiseSeedHtml(initialSeed)}
              <p class="detail-hint">Noise is visual only — original voxel colors stay stored. Set intensity to 0 to remove it.</p>
            </div>
          </details>
          <details class="detail-section" open>
            <summary>Geometry file</summary>
            <div class="detail-section-body">
              <p class="detail-hint">Download or upload .bfcb geometry. Uploading replaces the current voxels.</p>
              <div class="detail-actions">
                <button type="button" class="detail-secondary download-geometry">Download geometry</button>
                <button type="button" class="detail-secondary upload-geometry">Upload geometry</button>
                <input type="file" class="geometry-file" accept=".bfcb,.txt,text/plain" hidden>
              </div>
            </div>
          </details>
        </div>
        <div class="detail-tab-panel" data-panel="params" hidden>
          ${creating || !block ? '' : activeSnapshotHtml(block.views, block.activeViewId)}
          ${collisionSectionHtml(initialCollision, true)}
        </div>
        ${creating || !block ? '' : animateTabPanelHtml(block.views, block.animation, { allowOscillator: true })}
      </div>
      <div class="detail-footer">
        <p class="detail-error" role="alert"></p>
        <div class="detail-actions">
          <button type="button" class="detail-secondary cancel-detail">Cancel</button>
          <button type="button" class="detail-primary save-detail">Save</button>
          ${creating ? '' : `
            <button type="button" class="detail-danger delete-block">Delete block</button>`}
        </div>
      </div>`;
    wireDetailTabs(panel, this.detailTab, (tab) => {
      this.detailTab = tab;
    });
    const name = panel.querySelector<HTMLInputElement>('.field-name')!;
    const preview = panel.querySelector<HTMLElement>('.detail-preview')!;
    const preview3d = this.mountPreview3D(preview);
    const error = panel.querySelector<HTMLElement>('.detail-error')!;
    const readAnimation = !creating && block
      ? wireAnimationEditor(
        panel,
        block.views,
        block.animation ?? DEFAULT_BLOCK_ANIMATION,
        { allowOscillator: true },
      )
      : null;
    const fileInput = panel.querySelector<HTMLInputElement>('.geometry-file')!;
    const downloadButton = panel.querySelector<HTMLButtonElement>('.download-geometry')!;
    const noiseType = panel.querySelector<HTMLSelectElement>('.field-noise-type')!;
    const noiseBlend = panel.querySelector<HTMLSelectElement>('.field-noise-blend')!;
    const noiseIntensity = panel.querySelector<HTMLInputElement>('.field-noise-intensity')!;
    const noiseIntensityOut = panel.querySelector<HTMLOutputElement>('.noise-intensity-value')!;
    name.value = block?.name ?? `Custom block #${this.host.customBlocks.size + 1}`;
    let draft = {
      gridSize: block?.gridSize ?? CRAFT_GRID_SIZE,
      voxels: (block?.voxels ?? []).map(([index, material]) =>
        [index, material] as [number, VoxelMaterial]),
    };
    const noiseSeed = panel.querySelector<HTMLInputElement>('.field-noise-seed')!;
    const noiseSeedOut = panel.querySelector<HTMLOutputElement>('.noise-seed-value')!;
    const applyNoiseFields = (noise: BlockNoiseParams) => {
      noiseType.value = noise.type;
      noiseBlend.value = noise.blend;
      noiseIntensity.value = String(noise.intensity);
      noiseIntensityOut.value = `${noise.intensity}%`;
      const seed = clampNoiseSeed(noise.seed ?? (creating ? 0 : (block?.id ?? 0)));
      noiseSeed.value = String(seed);
      noiseSeedOut.value = String(seed);
    };
    const loadManageSnapshot = (viewId: number) => {
      const snap = block?.snapshots?.find((item) => item.id === viewId);
      if (!snap) return;
      draft = {
        gridSize: snap.gridSize,
        voxels: snap.voxels.map(([index, material]) =>
          [index, material] as [number, VoxelMaterial]),
      };
      applyNoiseFields(snap.noise ?? DEFAULT_BLOCK_NOISE);
    };
    if (!creating && block) {
      const manageId = this.resolveEditingViewId(block.views);
      this.editingViewId = manageId || null;
      loadManageSnapshot(manageId);
    } else {
      applyNoiseFields(initialNoise);
    }
    const readNoise = (): BlockNoiseParams => ({
      type: noiseType.value as BlockNoiseParams['type'],
      blend: noiseBlend.value as BlockNoiseParams['blend'],
      intensity: Math.max(0, Math.min(100, Number(noiseIntensity.value) || 0)),
      seed: clampNoiseSeed(Number(noiseSeed.value) || 0),
    });
    const readEditViewId = (): number | undefined => {
      const manage = panel.querySelector<HTMLSelectElement>('.field-manage-snapshot');
      const viewId = Number(manage?.value);
      if (Number.isInteger(viewId) && viewId > 0) {
        this.editingViewId = viewId;
        return viewId;
      }
      return this.editingViewId ?? undefined;
    };
    const redraw = () => {
      downloadButton.disabled = draft.voxels.length === 0;
      if (draft.voxels.length === 0) {
        preview3d.setSubject({
          kind: 'empty',
          message: 'Upload a .bfcb geometry file to preview this block.',
        });
        return;
      }
      const id = block?.id ?? 0;
      preview3d.setSubject({
        kind: 'custom',
        definition: {
          id,
          name: name.value,
          gridSize: draft.gridSize,
          voxels: draft.voxels,
          geometry: [],
          noise: readNoise(),
          collision: readCollision(panel, true),
          collectionId: block?.collectionId ?? null,
          views: block?.views ?? [],
          snapshots: block?.snapshots ?? [],
          activeViewId: block?.activeViewId ?? RANDOM_BLOCK_VIEW_ID,
          animation: block?.animation ?? DEFAULT_BLOCK_ANIMATION,
        },
      });
    };
    const onNoiseChange = () => {
      noiseIntensityOut.value = `${noiseIntensity.value}%`;
      noiseSeedOut.value = String(noiseSeed.value);
      redraw();
    };
    noiseType.onchange = onNoiseChange;
    noiseBlend.onchange = onNoiseChange;
    noiseIntensity.oninput = onNoiseChange;
    noiseSeed.oninput = onNoiseChange;
    redraw();
    panel.querySelector<HTMLButtonElement>('.cancel-detail')!.onclick = () => {
      this.view = { kind: 'tips' };
      this.fillGrid();
      this.renderDetail();
    };
    panel.querySelector<HTMLButtonElement>('.save-detail')!.onclick = async () => {
      const blockName = name.value.trim();
      if (!blockName) {
        error.textContent = 'Enter a block name.';
        return;
      }
      if (draft.voxels.length === 0) {
        error.textContent = 'Upload geometry before saving. Empty custom blocks are not saved.';
        return;
      }
      const noise = readNoise();
      const collision = readCollision(panel, true);
      try {
        if (creating) {
          const created = await this.host.createCustomBlock(
            blockName,
            draft.gridSize,
            draft.voxels,
            noise,
            collision,
          );
          this.view = { kind: 'custom', id: created.id };
        } else {
          const collectionSelect = panel.querySelector<HTMLSelectElement>('.field-collection');
          const geometryChanged =
            draft.gridSize !== block.gridSize ||
            !sameVoxels(draft.voxels, block.voxels);
          let updated: CustomBlockDefinition;
          const editViewId = readEditViewId();
          if (geometryChanged) {
            updated = await this.host.updateCustomBlock(
              block.id,
              blockName,
              draft.gridSize,
              draft.voxels,
              noise,
              collision,
              editViewId,
            );
            if (collectionSelect) {
              const collectionId = readCollectionSelect(collectionSelect);
              if (collectionId !== block.collectionId) {
                updated = await this.host.patchCustomBlock(block.id, {
                  name: blockName,
                  collectionId,
                  collision,
                  editViewId,
                });
              }
            }
          } else {
            updated = await this.host.patchCustomBlock(block.id, {
              name: blockName,
              collectionId: readCollectionSelect(collectionSelect),
              noise,
              collision,
              editViewId,
            });
          }
          if (readAnimation) {
            updated = await this.host.updateCustomBlockAnimation(block.id, readAnimation());
          }
          this.view = { kind: 'custom', id: updated.id };
        }
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not save block.';
      }
    };
    downloadButton.onclick = () => {
      if (draft.voxels.length === 0) return;
      downloadCustomBlockFile(name.value || block?.name || 'custom-block', draft.gridSize, draft.voxels);
    };
    panel.querySelector<HTMLButtonElement>('.upload-geometry')!.onclick = () => fileInput.click();
    fileInput.onchange = async () => {
      const file = fileInput.files?.[0];
      fileInput.value = '';
      if (!file) return;
      try {
        const parsed = await customBlockFileFromTextFile(file);
        draft = { gridSize: parsed.gridSize, voxels: parsed.voxels };
        const defaultName = `Custom block #${this.host.customBlocks.size + 1}`;
        if (
          parsed.name &&
          (!name.value.trim() || name.value === block?.name || name.value === defaultName)
        ) {
          name.value = parsed.name;
        }
        redraw();
        error.textContent =
          `Geometry loaded (${parsed.gridSize}³, ${parsed.voxels.length} voxels) — click Save to apply.`;
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not read geometry.';
      }
    };
    panel.querySelector<HTMLButtonElement>('.delete-block')?.addEventListener('click', async () => {
      if (!block) return;
      if (!window.confirm(`Remove “${block.name}” from your inventory? World placements stay.`)) {
        return;
      }
      try {
        await this.host.deleteCustomBlock(block.id);
        this.view = { kind: 'tips' };
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        window.alert(reason instanceof Error ? reason.message : 'Could not remove block.');
      }
    });
    if (!creating && block) {
      this.wireCustomViewSnapshots(panel, block, error, async () => {
        const blockName = name.value.trim();
        if (!blockName || draft.voxels.length === 0) {
          throw new Error('Save a named block with geometry before creating a snapshot.');
        }
        await this.host.updateCustomBlock(
          block.id,
          blockName,
          draft.gridSize,
          draft.voxels,
          readNoise(),
          readCollision(panel, true),
          readEditViewId(),
        );
      }, (viewId) => {
        loadManageSnapshot(viewId);
        redraw();
      });
    }
    name.focus();
    return panel;
  }

  /** View-tab snapshot id (never Params Random / Animation). */
  private resolveEditingViewId(views: BlockViewSnapshotRef[] | undefined): number {
    return defaultManageViewId(views, this.editingViewId ?? undefined);
  }

  private wireLibraryViewSnapshots(
    panel: HTMLElement,
    block: LibraryBlockDefinition,
    error: HTMLElement,
    saveActive: () => Promise<void>,
    onManageSelect: (viewId: number) => void,
  ): void {
    const manage = panel.querySelector<HTMLSelectElement>('.field-manage-snapshot');
    const active = panel.querySelector<HTMLSelectElement>('.field-active-snapshot');
    if (!manage || !active) return;
    const dirty = trackDetailDirty(panel);
    let manageViewId = Number(manage.value);
    manage.onchange = () => {
      const viewId = Number(manage.value);
      if (!Number.isInteger(viewId) || viewId < 1 || viewId === manageViewId) return;
      if (
        dirty.isDirty()
        && !window.confirm('Switch snapshot? Unsaved view edits will be lost.')
      ) {
        manage.value = String(manageViewId);
        return;
      }
      manageViewId = viewId;
      this.editingViewId = viewId;
      dirty.markClean();
      onManageSelect(viewId);
    };
    active.onchange = async () => {
      const viewId = Number(active.value);
      if (
        !Number.isInteger(viewId)
        || viewId < ANIMATION_BLOCK_VIEW_ID
        || viewId === block.activeViewId
      ) return;
      if (
        dirty.isDirty()
        && !window.confirm('Switch active snapshot? Unsaved view edits will be lost.')
      ) {
        active.value = String(block.activeViewId);
        return;
      }
      try {
        const updated = await this.host.activateLibraryBlockView(block.id, viewId);
        this.view = { kind: updated.kind, id: updated.id };
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        active.value = String(block.activeViewId);
        error.textContent = reason instanceof Error ? reason.message : 'Could not switch snapshot.';
      }
    };
    panel.querySelector<HTMLButtonElement>('.add-view-snapshot')!.onclick = async () => {
      const name = window.prompt('New snapshot name', 'Snapshot')?.trim();
      if (!name) return;
      try {
        await saveActive();
        const created = await this.host.createLibraryBlockView(
          block.id,
          name,
          manageViewId > 0 ? manageViewId : undefined,
        );
        const newest = created.views.find((view) => view.name === name)
          ?? created.views[created.views.length - 1];
        if (newest) this.editingViewId = newest.id;
        this.view = { kind: created.kind, id: created.id };
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not create snapshot.';
      }
    };
    panel.querySelector<HTMLButtonElement>('.rename-view-snapshot')!.onclick = async () => {
      const viewId = Number(manage.value);
      if (!Number.isInteger(viewId) || viewId < 1) return;
      const current = block.views.find((view) => view.id === viewId);
      const name = window.prompt('Rename snapshot', current?.name ?? '')?.trim();
      if (!name) return;
      try {
        const updated = await this.host.renameLibraryBlockView(block.id, viewId, name);
        this.view = { kind: updated.kind, id: updated.id };
        this.renderDetail();
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not rename snapshot.';
      }
    };
    panel.querySelector<HTMLButtonElement>('.delete-view-snapshot')!.onclick = async () => {
      const viewId = Number(manage.value);
      if (!Number.isInteger(viewId) || viewId < 1) return;
      const current = block.views.find((view) => view.id === viewId);
      if (!current) return;
      if (block.views.length <= 1) {
        error.textContent = 'Cannot delete the last snapshot.';
        return;
      }
      if (!window.confirm(`Delete snapshot “${current.name}”?`)) return;
      try {
        const updated = await this.host.deleteLibraryBlockView(block.id, viewId);
        this.view = { kind: updated.kind, id: updated.id };
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not delete snapshot.';
      }
    };
  }

  private wireCustomViewSnapshots(
    panel: HTMLElement,
    block: CustomBlockDefinition,
    error: HTMLElement,
    saveActive: () => Promise<void>,
    onManageSelect: (viewId: number) => void,
  ): void {
    const manage = panel.querySelector<HTMLSelectElement>('.field-manage-snapshot');
    const active = panel.querySelector<HTMLSelectElement>('.field-active-snapshot');
    if (!manage || !active) return;
    const dirty = trackDetailDirty(panel);
    let manageViewId = Number(manage.value);
    manage.onchange = () => {
      const viewId = Number(manage.value);
      if (!Number.isInteger(viewId) || viewId < 1 || viewId === manageViewId) return;
      if (
        dirty.isDirty()
        && !window.confirm('Switch snapshot? Unsaved view edits will be lost.')
      ) {
        manage.value = String(manageViewId);
        return;
      }
      manageViewId = viewId;
      this.editingViewId = viewId;
      dirty.markClean();
      onManageSelect(viewId);
    };
    active.onchange = async () => {
      const viewId = Number(active.value);
      if (
        !Number.isInteger(viewId)
        || viewId < ANIMATION_BLOCK_VIEW_ID
        || viewId === block.activeViewId
      ) return;
      if (
        dirty.isDirty()
        && !window.confirm('Switch active snapshot? Unsaved view edits will be lost.')
      ) {
        active.value = String(block.activeViewId);
        return;
      }
      try {
        await this.host.activateCustomBlockView(block.id, viewId);
        this.view = { kind: 'custom', id: block.id };
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        active.value = String(block.activeViewId);
        error.textContent = reason instanceof Error ? reason.message : 'Could not switch snapshot.';
      }
    };
    panel.querySelector<HTMLButtonElement>('.add-view-snapshot')!.onclick = async () => {
      const name = window.prompt('New snapshot name', 'Snapshot')?.trim();
      if (!name) return;
      try {
        await saveActive();
        const created = await this.host.createCustomBlockView(
          block.id,
          name,
          manageViewId > 0 ? manageViewId : undefined,
        );
        const newest = created.views.find((view) => view.name === name)
          ?? created.views[created.views.length - 1];
        if (newest) this.editingViewId = newest.id;
        this.view = { kind: 'custom', id: created.id };
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not create snapshot.';
      }
    };
    panel.querySelector<HTMLButtonElement>('.rename-view-snapshot')!.onclick = async () => {
      const viewId = Number(manage.value);
      if (!Number.isInteger(viewId) || viewId < 1) return;
      const current = block.views.find((view) => view.id === viewId);
      const name = window.prompt('Rename snapshot', current?.name ?? '')?.trim();
      if (!name) return;
      try {
        await this.host.renameCustomBlockView(block.id, viewId, name);
        this.view = { kind: 'custom', id: block.id };
        this.renderDetail();
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not rename snapshot.';
      }
    };
    panel.querySelector<HTMLButtonElement>('.delete-view-snapshot')!.onclick = async () => {
      const viewId = Number(manage.value);
      if (!Number.isInteger(viewId) || viewId < 1) return;
      const current = block.views.find((view) => view.id === viewId);
      if (!current) return;
      if (block.views.length <= 1) {
        error.textContent = 'Cannot delete the last snapshot.';
        return;
      }
      if (!window.confirm(`Delete snapshot “${current.name}”?`)) return;
      try {
        await this.host.deleteCustomBlockView(block.id, viewId);
        this.view = { kind: 'custom', id: block.id };
        this.fillGrid();
        this.renderDetail();
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Could not delete snapshot.';
      }
    };
  }

  private builtinForm(type: number): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'inventory-detail has-pinned-preview';
    const name = blockTypeById(type).name;
    panel.innerHTML = `
      <div class="detail-preview-pin">
        <div class="detail-preview color-block-preview"></div>
      </div>
      <div class="detail-body">
        <h2>${escapeHtml(name)}</h2>
        <details class="detail-section" open>
          <summary>Common</summary>
          <div class="detail-section-body">
            <p>Core collection block. Drag it to quick access.</p>
            <p>This block cannot be edited, renamed, moved, or removed.</p>
          </div>
        </details>
      </div>`;
    this.mountPreview3D(panel.querySelector<HTMLElement>('.detail-preview')!)
      .setSubject({ kind: 'builtin', type });
    return panel;
  }

  private collectionSelectHtml(selected: number | null): string {
    const options = [
      '<option value="">None</option>',
      ...[...this.host.collections.values()]
        .filter((collection) => !isExternalCollection(collection))
        .sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id)
        .map((collection) =>
          `<option value="${collection.id}"${
            collection.id === selected ? ' selected' : ''
          }>${escapeHtml(collection.name)}</option>`),
    ];
    return `<select class="field-collection">${options.join('')}</select>`;
  }

  private async removeLibrary(id: number, name: string): Promise<void> {
    if (!window.confirm(`Remove “${name}” from your inventory? World placements stay.`)) return;
    try {
      await this.host.deleteLibraryBlock(id);
      this.view = { kind: 'tips' };
      this.fillGrid();
      this.renderDetail();
    } catch (reason) {
      window.alert(reason instanceof Error ? reason.message : 'Could not remove block.');
    }
  }
}

interface DragPayload {
  source: 'inventory' | 'quick';
  type: number;
  slot?: number;
}

function isExternalCollection(collection: BlockCollection): boolean {
  return collection.external === true;
}

function accessLabel(access: CollectionAccess): string {
  if (access === 'friends') return 'Friends';
  if (access === 'public') return 'Public';
  return 'Private';
}

function accessIcon(access: CollectionAccess): string {
  if (access === 'friends') return 'group';
  if (access === 'public') return 'public';
  return 'lock';
}

type DetailTabId = 'view' | 'params' | 'animate';

function detailTabsHtml(
  includeAnimate = false,
  activeTab: DetailTabId = 'view',
): string {
  const tab = includeAnimate || activeTab !== 'animate' ? activeTab : 'view';
  const tabButton = (id: DetailTabId, label: string) => {
    const active = id === tab;
    return `<button type="button" class="detail-tab${
      active ? ' active' : ''
    }" data-tab="${id}" role="tab" aria-selected="${active ? 'true' : 'false'}">${label}</button>`;
  };
  return `
    <div class="detail-tabs" role="tablist">
      ${tabButton('view', 'View')}
      ${tabButton('params', 'Params')}
      ${includeAnimate ? tabButton('animate', 'Animate') : ''}
    </div>`;
}

/** Named snapshot selected on the View tab (never Random / Animation). */
function defaultManageViewId(
  views: BlockViewSnapshotRef[] | undefined,
  preferredId: number | undefined,
): number {
  const list = views ?? [];
  if (list.length === 0) return 0;
  if (preferredId != null && list.some((view) => view.id === preferredId)) {
    return preferredId;
  }
  return list[0]!.id;
}

/** View tab: create / rename / delete snapshots (no active-mode control). */
function viewSnapshotsManageHtml(
  views: BlockViewSnapshotRef[] | undefined,
  manageId: number,
): string {
  const list = views ?? [];
  if (list.length === 0) return '';
  return `
    <details class="detail-section" open>
      <summary>Snapshots</summary>
      <div class="detail-section-body">
        <label>Snapshot
          <select class="field-manage-snapshot">
            ${list.map((view) =>
              `<option value="${view.id}"${
                view.id === manageId ? ' selected' : ''
              }>${escapeHtml(view.name)}</option>`).join('')}
          </select>
        </label>
        <div class="detail-actions snapshot-actions">
          <button type="button" class="detail-secondary add-view-snapshot">New</button>
          <button type="button" class="detail-secondary rename-view-snapshot">Rename</button>
          <button type="button" class="detail-danger delete-view-snapshot"${
            list.length <= 1 ? ' disabled' : ''
          }>Delete</button>
        </div>
        <p class="detail-hint">Manage named View variants here. Choose which one is active (or Random / Animation) on the Params tab. Up to ${MAX_BLOCK_VIEW_SNAPSHOTS} per block.</p>
      </div>
    </details>`;
}

/** Params tab: choose active snapshot / Random / Animation. */
function activeSnapshotHtml(
  views: BlockViewSnapshotRef[] | undefined,
  activeViewId: number | undefined,
): string {
  const list = views ?? [];
  if (list.length === 0) return '';
  const active = activeViewId ?? list[0]!.id;
  const randomSelected = active === RANDOM_BLOCK_VIEW_ID;
  const animationSelected = active === ANIMATION_BLOCK_VIEW_ID;
  return `
    <details class="detail-section" open>
      <summary>Active snapshot</summary>
      <div class="detail-section-body">
        <label>Mode
          <select class="field-active-snapshot">
            <option value="${RANDOM_BLOCK_VIEW_ID}"${
              randomSelected ? ' selected' : ''
            }>Random</option>
            <option value="${ANIMATION_BLOCK_VIEW_ID}"${
              animationSelected ? ' selected' : ''
            }>Animation</option>
            ${list.map((view) =>
              `<option value="${view.id}"${
                view.id === active ? ' selected' : ''
              }>${escapeHtml(view.name)}</option>`).join('')}
          </select>
        </label>
        <p class="detail-hint">Named snapshots load into View for editing. Random picks one at place time. Animation plays the Animate tab loop (sequence or parametric).${
          animationSelected || randomSelected
            ? ' While Random/Animation is active, Save does not rewrite snapshot View data.'
            : ''
        }</p>
      </div>
    </details>`;
}

function clampNoiseSeed(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

function noiseSeedHtml(seed: number): string {
  const value = clampNoiseSeed(seed);
  return `
    <label>Seed
      <output class="noise-seed-value">${value}</output>
      <input class="field-noise-seed" type="range" min="0" max="100" step="1" value="${value}">
    </label>
    <p class="detail-hint">Change the seed to reshuffle noise while keeping type, blend, and intensity.</p>`;
}

function collisionSectionHtml(
  initial: BlockCollisionParams,
  includeShrink: boolean,
): string {
  // Collision group UI is temporarily hidden; keep stored group via data attr.
  return `
    <details class="detail-section" open>
      <summary>Collision</summary>
      <div class="detail-section-body" data-collision-group="${initial.group}">
        <label class="detail-check">
          <input type="checkbox" class="field-collide-player"${
            initial.collideWithPlayer ? ' checked' : ''}>
          <span>Collide with players</span>
        </label>
        ${includeShrink ? `
          <label class="detail-check">
            <input type="checkbox" class="field-shrink-hitbox"${
              initial.shrinkHitbox ? ' checked' : ''}>
            <span>Shrink hitbox to visible voxels</span>
          </label>
          <p class="detail-hint">Placement stays on the 1 m grid. Only the player collider shrinks to the occupied voxel bounds.</p>
        ` : `
          <p class="detail-hint">Build targeting still uses the full cell.</p>
        `}
      </div>
    </details>`;
}

/** Tracks unsaved form edits so snapshot switch only warns when needed. */
function trackDetailDirty(panel: HTMLElement): {
  isDirty: () => boolean;
  markDirty: () => void;
  markClean: () => void;
} {
  let dirty = false;
  const markDirty = () => {
    dirty = true;
  };
  const markClean = () => {
    dirty = false;
  };
  const shouldIgnore = (target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest('.field-manage-snapshot')
      || target.closest('.field-active-snapshot')
      || target.closest('.detail-footer')
      || target.closest('.snapshot-actions')
      || target.closest('.detail-tabs'),
    );
  };
  panel.addEventListener('input', (event) => {
    if (!shouldIgnore(event.target)) markDirty();
  });
  panel.addEventListener('change', (event) => {
    if (!shouldIgnore(event.target)) markDirty();
  });
  // Swatch buttons assign .value without firing input/change.
  panel.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.color-swatches')) markDirty();
  });
  return { isDirty: () => dirty, markDirty, markClean };
}

function wireDetailTabs(
  panel: HTMLElement,
  initialTab: DetailTabId = 'view',
  onTabChange?: (tab: DetailTabId) => void,
): void {
  const tabs = [...panel.querySelectorAll<HTMLButtonElement>('.detail-tab')];
  const panels = [...panel.querySelectorAll<HTMLElement>('.detail-tab-panel')];
  const body = panel.querySelector<HTMLElement>('.detail-body');
  const preview = panel.querySelector<HTMLElement>('[data-view-preview]');
  const showTab = (id: DetailTabId, notify: boolean) => {
    const available = tabs.some((button) => button.dataset.tab === id);
    const next: DetailTabId = available ? id : 'view';
    for (const button of tabs) {
      const active = button.dataset.tab === next;
      button.classList.toggle('active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    }
    for (const pane of panels) {
      pane.hidden = pane.dataset.panel !== next;
    }
    if (preview) preview.hidden = next !== 'view';
    if (body) body.scrollTop = 0;
    if (next === 'view') {
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    }
    if (notify) onTabChange?.(next);
  };
  for (const tab of tabs) {
    tab.onclick = () => {
      const id = tab.dataset.tab;
      if (id === 'view' || id === 'params' || id === 'animate') showTab(id, true);
    };
  }
  showTab(initialTab, false);
}

interface AnimationEditorOptions {
  allowParametric?: boolean;
  allowOscillator?: boolean;
}

function resolveAnimType(
  animation: BlockAnimation,
  options: AnimationEditorOptions,
): BlockAnimation['type'] {
  if (animation.type === 'oscillator' && options.allowOscillator) return 'oscillator';
  if (animation.type === 'parametric' && options.allowParametric) return 'parametric';
  return 'sequence';
}

function animationTypeHint(type: BlockAnimation['type']): string {
  if (type === 'parametric') {
    return 'Build a keyframe loop. Each step’s ticks are the blend duration from that snapshot to the next (and the last back to the first). Color, transparency, noise intensity, and seed lerp smoothly. Best for solid-fill textured blocks with noise. Set Params → Active snapshot to “Animation”.';
  }
  if (type === 'oscillator') {
    return `Add oscillators that deform voxels visually (hitbox stays the rest pose). Each oscillator uses one axis (X/Y/Z); stack oscillators for multi-axis motion. Displacement grows with distance from the anchor. Period is 1–${MAX_OSCILLATOR_PERIOD_TICKS} ticks (20 ticks/sec). Set Params → Active snapshot to “Animation”.`;
  }
  return `Drag or click snapshots to build a loop. Each step holds that snapshot for 0–${MAX_SEQUENCE_ITEM_TICKS} ticks (20 ticks per second), then switches discretely. Use for complex frame-by-frame textures. Set Params → Active snapshot to “Animation”.`;
}

function animateTabPanelHtml(
  views: BlockViewSnapshotRef[],
  animation: BlockAnimation,
  options: AnimationEditorOptions,
): string {
  const allowParametric = options.allowParametric === true;
  const allowOscillator = options.allowOscillator === true;
  const sync = animation.sync ? ' checked' : '';
  const type = resolveAnimType(animation, options);

  if (views.length === 0 && !allowOscillator) {
    return `
      <div class="detail-tab-panel" data-panel="animate" hidden>
        <p class="detail-hint">Create at least one snapshot on the View tab before editing animation.</p>
      </div>`;
  }

  // If only oscillator is available (no views), force selection.
  const effectiveType = views.length === 0 && allowOscillator ? 'oscillator' : type;
  const typeOptions: string[] = [];
  if (views.length > 0) {
    typeOptions.push(
      `<option value="sequence"${effectiveType === 'sequence' ? ' selected' : ''}>Sequence Animation</option>`,
    );
  }
  if (allowParametric && views.length > 0) {
    typeOptions.push(
      `<option value="parametric"${effectiveType === 'parametric' ? ' selected' : ''}>Parametric Animation</option>`,
    );
  }
  if (allowOscillator) {
    typeOptions.push(
      `<option value="oscillator"${effectiveType === 'oscillator' ? ' selected' : ''}>Oscillator Animation</option>`,
    );
  }
  const hint = animationTypeHint(effectiveType);
  const sequenceHidden = effectiveType === 'oscillator' ? ' hidden' : '';
  const oscillatorHidden = effectiveType === 'oscillator' ? '' : ' hidden';

  return `
    <div class="detail-tab-panel" data-panel="animate" hidden>
      <details class="detail-section" open>
        <summary>Animation</summary>
        <div class="detail-section-body">
          <label>Type
            <select class="field-animation-type">
              ${typeOptions.join('\n              ')}
            </select>
          </label>
          <label class="detail-check">
            <input type="checkbox" class="field-animation-sync"${sync}>
            <span>Sync to world ticker</span>
          </label>
          <p class="detail-hint">Synced animations follow the global world clock so every placement stays in phase. Unsynced placements start their loop when placed.</p>
          <div class="sequence-editor"${sequenceHidden}>
            <h3 class="sequence-heading">Available snapshots</h3>
            <div class="sequence-palette" role="list" aria-label="Available snapshots"></div>
            <h3 class="sequence-heading sequence-list-heading">${
              effectiveType === 'parametric' ? 'Keyframes' : 'Sequence'
            }</h3>
            <ol class="sequence-list" role="list" aria-label="Animation sequence"></ol>
          </div>
          <div class="oscillator-editor"${oscillatorHidden}>
            <h3 class="sequence-heading">Oscillators</h3>
            <ul class="oscillator-list" role="list" aria-label="Oscillators"></ul>
            <button type="button" class="detail-secondary add-oscillator">Add oscillator</button>
          </div>
          <p class="detail-hint animation-type-hint">${hint}</p>
        </div>
      </details>
    </div>`;
}

function wireAnimationEditor(
  panel: HTMLElement,
  views: BlockViewSnapshotRef[],
  initial: BlockAnimation,
  options: AnimationEditorOptions,
): () => BlockAnimation {
  const allowParametric = options.allowParametric === true;
  const allowOscillator = options.allowOscillator === true;
  const syncInput = panel.querySelector<HTMLInputElement>('.field-animation-sync');
  const typeSelect = panel.querySelector<HTMLSelectElement>('.field-animation-type');
  const sequenceEditor = panel.querySelector<HTMLElement>('.sequence-editor');
  const oscillatorEditor = panel.querySelector<HTMLElement>('.oscillator-editor');
  const palette = panel.querySelector<HTMLElement>('.sequence-palette');
  const list = panel.querySelector<HTMLElement>('.sequence-list');
  const oscillatorList = panel.querySelector<HTMLElement>('.oscillator-list');
  const addOscillatorBtn = panel.querySelector<HTMLButtonElement>('.add-oscillator');
  const heading = panel.querySelector<HTMLElement>('.sequence-list-heading');
  const hint = panel.querySelector<HTMLElement>('.animation-type-hint');

  if (!syncInput) {
    return () => ({ ...DEFAULT_BLOCK_ANIMATION, items: [] });
  }

  let animType: BlockAnimation['type'] = resolveAnimType(initial, options);
  if (views.length === 0 && allowOscillator) animType = 'oscillator';

  let items: SequenceAnimationItem[] =
    initial.type === 'sequence' || initial.type === 'parametric'
      ? initial.items.map((item) => ({ ...item }))
      : [];
  let oscillators: BlockOscillator[] =
    initial.type === 'oscillator'
      ? initial.oscillators.map((item) => ({
        ...DEFAULT_OSCILLATOR,
        ...item,
        axis: item.axis ?? DEFAULT_OSCILLATOR.axis,
      }))
      : [];
  let dragFromIndex: number | null = null;
  let dragViewId: number | null = null;

  const viewName = (viewId: number): string =>
    views.find((view) => view.id === viewId)?.name ?? `View #${viewId}`;

  const notifyChange = () => {
    (list ?? oscillatorList)?.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const clampTicks = (value: number): number =>
    Math.max(0, Math.min(MAX_SEQUENCE_ITEM_TICKS, Math.round(value) || 0));

  const clampPeriod = (value: number): number =>
    Math.max(1, Math.min(MAX_OSCILLATOR_PERIOD_TICKS, Math.round(value) || 1));

  const clampPhaseOffset = (value: number): number =>
    Math.max(0, Math.min(MAX_OSCILLATOR_PERIOD_TICKS, Math.round(value) || 0));

  const clampAmplitude = (value: number): number =>
    Math.max(0, Math.min(MAX_OSCILLATOR_AMPLITUDE, value));

  const updateTypeChrome = (): void => {
    if (heading) heading.textContent = animType === 'parametric' ? 'Keyframes' : 'Sequence';
    if (hint) hint.textContent = animationTypeHint(animType);
    if (sequenceEditor) sequenceEditor.hidden = animType === 'oscillator';
    if (oscillatorEditor) oscillatorEditor.hidden = animType !== 'oscillator';
  };

  const insertItem = (viewId: number, index: number): void => {
    if (items.length >= MAX_SEQUENCE_ITEMS) return;
    if (!views.some((view) => view.id === viewId)) return;
    const next = [...items];
    next.splice(Math.max(0, Math.min(index, next.length)), 0, { viewId, ticks: 20 });
    items = next;
    renderSequence();
    notifyChange();
  };

  const moveItem = (from: number, to: number): void => {
    if (from === to || from < 0 || from >= items.length) return;
    const next = [...items];
    const [removed] = next.splice(from, 1);
    if (!removed) return;
    const target = from < to ? to - 1 : to;
    next.splice(Math.max(0, Math.min(target, next.length)), 0, removed);
    items = next;
    renderSequence();
    notifyChange();
  };

  const dropIndexFromEvent = (event: DragEvent): number => {
    const target = (event.target as Element | null)?.closest<HTMLElement>('.sequence-item');
    if (!target) return items.length;
    const index = Number(target.dataset.index);
    if (!Number.isInteger(index)) return items.length;
    const rect = target.getBoundingClientRect();
    const after = event.clientY > rect.top + rect.height / 2;
    return after ? index + 1 : index;
  };

  const renderSequence = (): void => {
    if (!palette || !list) return;
    palette.replaceChildren();
    for (const view of views) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'sequence-chip';
      chip.textContent = view.name;
      chip.draggable = true;
      chip.dataset.viewId = String(view.id);
      chip.title = `Add ${view.name} to sequence`;
      chip.ondragstart = (event) => {
        dragViewId = view.id;
        dragFromIndex = null;
        event.dataTransfer?.setData('application/x-sequence-view', String(view.id));
        event.dataTransfer!.effectAllowed = 'copy';
        chip.classList.add('dragging');
      };
      chip.ondragend = () => {
        dragViewId = null;
        chip.classList.remove('dragging');
        list.classList.remove('drag-over');
      };
      chip.onclick = () => insertItem(view.id, items.length);
      palette.appendChild(chip);
    }

    list.replaceChildren();
    items.forEach((item, index) => {
      const row = document.createElement('li');
      row.className = 'sequence-item';
      row.draggable = true;
      row.dataset.index = String(index);
      const ticksLabel = animType === 'parametric' ? 'Blend ticks' : 'Ticks';
      row.innerHTML = `
        <span class="sequence-item-name">${escapeHtml(viewName(item.viewId))}</span>
        <label class="sequence-item-ticks">${ticksLabel}
          <input type="number" class="field-sequence-ticks" min="0" max="${MAX_SEQUENCE_ITEM_TICKS}" value="${item.ticks}">
        </label>
        <button type="button" class="detail-secondary sequence-item-remove" aria-label="Remove step">×</button>`;
      const ticksInput = row.querySelector<HTMLInputElement>('.field-sequence-ticks')!;
      ticksInput.oninput = () => {
        items[index] = { ...items[index]!, ticks: clampTicks(Number(ticksInput.value)) };
        notifyChange();
      };
      row.querySelector<HTMLButtonElement>('.sequence-item-remove')!.onclick = () => {
        items = items.filter((_, i) => i !== index);
        renderSequence();
        notifyChange();
      };
      row.ondragstart = (event) => {
        dragFromIndex = index;
        dragViewId = null;
        event.dataTransfer?.setData('application/x-sequence-index', String(index));
        event.dataTransfer!.effectAllowed = 'move';
        row.classList.add('dragging');
      };
      row.ondragend = () => {
        dragFromIndex = null;
        row.classList.remove('dragging');
        list.classList.remove('drag-over');
      };
      list.appendChild(row);
    });
  };

  const renderOscillators = (): void => {
    if (!oscillatorList) return;
    oscillatorList.replaceChildren();
    oscillators.forEach((oscillator, index) => {
      const row = document.createElement('li');
      row.className = 'oscillator-item';
      row.dataset.index = String(index);
      const ampPercent = Math.round(oscillator.amplitude * 100);
      row.innerHTML = `
        <div class="oscillator-item-header">
          <span class="oscillator-item-title">Oscillator ${index + 1}</span>
          <button type="button" class="detail-secondary oscillator-item-remove" aria-label="Remove oscillator">×</button>
        </div>
        <div class="oscillator-mode-axis">
          <div class="oscillator-field">
            <span class="oscillator-field-label">Mode</span>
            <select class="field-oscillator-mode" aria-label="Oscillator mode">
              ${OSCILLATOR_MODES.map((mode) =>
                `<option value="${mode}"${mode === oscillator.mode ? ' selected' : ''}>${
                  mode.charAt(0).toUpperCase() + mode.slice(1)
                }</option>`).join('')}
            </select>
          </div>
          <div class="oscillator-field">
            <span class="oscillator-field-label">Axis</span>
            <div class="axis-picker" role="radiogroup" aria-label="Deformation axis"></div>
          </div>
        </div>
        <div class="oscillator-anchor-field">
          <span class="oscillator-anchor-label">Anchor</span>
          <div class="anchor-picker" role="radiogroup" aria-label="Oscillator anchor"></div>
        </div>
        <div class="oscillator-timing-row">
          <label>Period (ticks)
            <input type="number" class="field-oscillator-period" min="1" max="${MAX_OSCILLATOR_PERIOD_TICKS}" value="${oscillator.period}">
          </label>
          <label>Phase offset (ticks)
            <input type="number" class="field-oscillator-phase" min="0" max="${MAX_OSCILLATOR_PERIOD_TICKS}" value="${oscillator.phaseOffset}">
          </label>
        </div>
        <label>Amplitude
          <input type="range" class="field-oscillator-amplitude" min="0" max="100" value="${ampPercent}">
          <output class="oscillator-amplitude-value">${ampPercent}%</output>
        </label>`;

      const modeSelect = row.querySelector<HTMLSelectElement>('.field-oscillator-mode')!;
      modeSelect.onchange = () => {
        const mode = modeSelect.value as OscillatorMode;
        if ((OSCILLATOR_MODES as readonly string[]).includes(mode)) {
          oscillators[index] = { ...oscillators[index]!, mode };
          notifyChange();
        }
      };

      const axisPicker = row.querySelector<HTMLElement>('.axis-picker')!;
      for (const axis of OSCILLATOR_AXES) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `axis-option${axis === oscillator.axis ? ' selected' : ''}`;
        btn.dataset.axis = axis;
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', axis === oscillator.axis ? 'true' : 'false');
        btn.title = `Axis ${axis.toUpperCase()}`;
        btn.textContent = axis.toUpperCase();
        btn.onclick = () => {
          oscillators[index] = { ...oscillators[index]!, axis: axis as OscillatorAxis };
          renderOscillators();
          notifyChange();
        };
        axisPicker.appendChild(btn);
      }

      const periodInput = row.querySelector<HTMLInputElement>('.field-oscillator-period')!;
      periodInput.oninput = () => {
        oscillators[index] = {
          ...oscillators[index]!,
          period: clampPeriod(Number(periodInput.value)),
        };
        notifyChange();
      };

      const phaseInput = row.querySelector<HTMLInputElement>('.field-oscillator-phase')!;
      phaseInput.oninput = () => {
        oscillators[index] = {
          ...oscillators[index]!,
          phaseOffset: clampPhaseOffset(Number(phaseInput.value)),
        };
        notifyChange();
      };

      const ampInput = row.querySelector<HTMLInputElement>('.field-oscillator-amplitude')!;
      const ampOut = row.querySelector<HTMLOutputElement>('.oscillator-amplitude-value')!;
      ampInput.oninput = () => {
        const percent = Math.max(0, Math.min(100, Number(ampInput.value) || 0));
        ampOut.textContent = `${percent}%`;
        oscillators[index] = {
          ...oscillators[index]!,
          amplitude: clampAmplitude(percent / 100),
        };
        notifyChange();
      };

      const picker = row.querySelector<HTMLElement>('.anchor-picker')!;
      for (const anchor of OSCILLATOR_ANCHORS) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `anchor-option${anchor === oscillator.anchor ? ' selected' : ''}`;
        btn.dataset.anchor = anchor;
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', anchor === oscillator.anchor ? 'true' : 'false');
        btn.title = anchorLabel(anchor);
        btn.innerHTML = `${anchorIconSvg(anchor)}<span>${escapeHtml(anchorLabel(anchor))}</span>`;
        btn.onclick = () => {
          oscillators[index] = { ...oscillators[index]!, anchor };
          renderOscillators();
          notifyChange();
        };
        picker.appendChild(btn);
      }

      row.querySelector<HTMLButtonElement>('.oscillator-item-remove')!.onclick = () => {
        oscillators = oscillators.filter((_, i) => i !== index);
        renderOscillators();
        notifyChange();
      };
      oscillatorList.appendChild(row);
    });
    if (addOscillatorBtn) {
      addOscillatorBtn.disabled = oscillators.length >= MAX_OSCILLATORS;
    }
  };

  if (list) {
    list.ondragover = (event) => {
      if (!event.dataTransfer?.types.includes('application/x-sequence-view')
        && !event.dataTransfer?.types.includes('application/x-sequence-index')) {
        return;
      }
      event.preventDefault();
      list.classList.add('drag-over');
      event.dataTransfer.dropEffect = dragFromIndex === null ? 'copy' : 'move';
    };
    list.ondragleave = (event) => {
      if (event.currentTarget === event.target) list.classList.remove('drag-over');
    };
    list.ondrop = (event) => {
      event.preventDefault();
      list.classList.remove('drag-over');
      const dropIndex = dropIndexFromEvent(event);
      const paletteView = event.dataTransfer?.getData('application/x-sequence-view');
      if (paletteView) {
        insertItem(Number(paletteView), dropIndex);
        return;
      }
      const fromRaw = event.dataTransfer?.getData('application/x-sequence-index');
      if (fromRaw !== '') {
        const from = Number(fromRaw);
        if (Number.isInteger(from)) moveItem(from, dropIndex);
      } else if (dragViewId !== null) {
        insertItem(dragViewId, dropIndex);
      } else if (dragFromIndex !== null) {
        moveItem(dragFromIndex, dropIndex);
      }
    };
  }

  if (addOscillatorBtn) {
    addOscillatorBtn.onclick = () => {
      if (oscillators.length >= MAX_OSCILLATORS) return;
      oscillators = [...oscillators, { ...DEFAULT_OSCILLATOR }];
      renderOscillators();
      notifyChange();
    };
  }

  if (typeSelect) {
    typeSelect.onchange = () => {
      const value = typeSelect.value;
      if (value === 'oscillator' && allowOscillator) animType = 'oscillator';
      else if (value === 'parametric' && allowParametric) animType = 'parametric';
      else animType = 'sequence';
      updateTypeChrome();
      if (animType === 'oscillator') renderOscillators();
      else renderSequence();
      notifyChange();
    };
  }

  updateTypeChrome();
  if (animType === 'oscillator') renderOscillators();
  else renderSequence();

  return (): BlockAnimation => {
    if (animType === 'oscillator') {
      const next: BlockOscillatorAnimation = {
        type: 'oscillator',
        sync: syncInput.checked,
        oscillators: oscillators.slice(0, MAX_OSCILLATORS).map((item) => ({
          mode: item.mode,
          axis: item.axis,
          anchor: item.anchor,
          period: clampPeriod(item.period),
          phaseOffset: clampPhaseOffset(item.phaseOffset),
          amplitude: clampAmplitude(item.amplitude),
        })),
      };
      return next;
    }
    const nextItems = items
      .filter((item) => views.some((view) => view.id === item.viewId))
      .slice(0, MAX_SEQUENCE_ITEMS)
      .map((item) => ({
        viewId: item.viewId,
        ticks: clampTicks(item.ticks),
      }));
    if (animType === 'parametric') {
      const parametric: BlockParametricAnimation = {
        type: 'parametric',
        sync: syncInput.checked,
        items: nextItems,
      };
      return parametric;
    }
    const sequence: BlockSequenceAnimation = {
      type: 'sequence',
      sync: syncInput.checked,
      items: nextItems,
    };
    return sequence;
  };
}

function anchorLabel(anchor: OscillatorAnchor): string {
  switch (anchor) {
    case 'center': return 'Center';
    case 'bottom': return 'Bottom';
    case 'top': return 'Top';
    case 'north': return 'North';
    case 'south': return 'South';
    case 'east': return 'East';
    case 'west': return 'West';
  }
}

/** Wireframe cube with accented anchor point (isometric-ish). */
function anchorIconSvg(anchor: OscillatorAnchor): string {
  const points: Record<OscillatorAnchor, [number, number]> = {
    center: [24, 24],
    bottom: [24, 34],
    top: [24, 14],
    north: [24, 17],
    south: [24, 31],
    east: [33, 24],
    west: [15, 24],
  };
  const [cx, cy] = points[anchor];
  return `
    <svg class="anchor-icon" viewBox="0 0 48 48" aria-hidden="true">
      <path d="M24 8 L40 17 L40 31 L24 40 L8 31 L8 17 Z" fill="none" stroke="currentColor" stroke-width="1.6"/>
      <path d="M24 8 L24 24 M8 17 L24 24 L40 17 M24 24 L24 40" fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.55"/>
      <circle cx="${cx}" cy="${cy}" r="3.4" fill="#087a22" stroke="#171717" stroke-width="1"/>
    </svg>`;
}

function readCollision(panel: HTMLElement, includeShrink: boolean): BlockCollisionParams {
  const collide = panel.querySelector<HTMLInputElement>('.field-collide-player');
  const shrink = panel.querySelector<HTMLInputElement>('.field-shrink-hitbox');
  const storedGroup = Number(
    panel.querySelector<HTMLElement>('[data-collision-group]')?.dataset.collisionGroup ?? 0,
  );
  return {
    collideWithPlayer: collide?.checked !== false,
    group: BLOCK_COLLISION_GROUPS.some((entry) => entry.id === storedGroup)
      ? storedGroup as 0 | 1 | 2 | 3
      : 0,
    shrinkHitbox: includeShrink && shrink?.checked === true,
  };
}

function solidFillTexture(
  color: string,
  gridSize: number = TEXTURE_GRID_SIZE,
): TexturePixels {
  const cellCount = gridSize * gridSize;
  return Array.from({ length: 6 }, () =>
    Array.from({ length: cellCount }, () => color));
}

function sameVoxels(
  a: Array<[number, VoxelMaterial]>,
  b: Array<[number, VoxelMaterial]>,
): boolean {
  if (a.length !== b.length) return false;
  const map = new Map(b);
  for (const [index, material] of a) {
    if (map.get(index) !== material) return false;
  }
  return true;
}

function normalizeHex(value: string): string | null {
  const color = value.trim().toLowerCase();
  return /^#[0-9a-f]{6}$/.test(color) ? color : null;
}

function alphaHex(opacity: number): string {
  return Math.round(Math.max(0, Math.min(1, opacity)) * 255)
    .toString(16)
    .padStart(2, '0');
}

function readCollectionSelect(select: HTMLSelectElement | null): number | null {
  if (!select || !select.value) return null;
  const id = Number(select.value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function allowDrop(event: DragEvent): void {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
}

function setDragPayload(data: DataTransfer, payload: DragPayload): void {
  data.effectAllowed = 'move';
  data.setData('application/x-blankforge-block', JSON.stringify(payload));
  data.setData('text/plain', JSON.stringify(payload));
}

/** Drag ghost: block preview only, centered on the cursor (no card chrome). */
function setBlockDragImage(event: DragEvent, sourceIcon: HTMLCanvasElement): void {
  if (!event.dataTransfer) return;
  const size = 72;
  const ghost = document.createElement('canvas');
  ghost.className = 'block-drag-ghost';
  ghost.width = sourceIcon.width;
  ghost.height = sourceIcon.height;
  ghost.getContext('2d')!.drawImage(sourceIcon, 0, 0);
  ghost.style.cssText =
    `position:fixed;top:-9999px;left:-9999px;width:${size}px;height:${size}px;`
    + 'pointer-events:none;background:transparent;';
  document.body.appendChild(ghost);
  event.dataTransfer.setDragImage(ghost, size / 2, size / 2);
  requestAnimationFrame(() => ghost.remove());
}

function readDragPayload(data: DataTransfer | null): DragPayload | null {
  if (!data) return null;
  const raw =
    data.getData('application/x-blankforge-block') ||
    data.getData('text/plain');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as DragPayload;
  } catch {
    return null;
  }
}

export type { DragPayload };
export { allowDrop, setDragPayload, setBlockDragImage, readDragPayload };
